import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  Divider,
  Card,
  CardContent,
  Alert,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  styled,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  RestartAlt as RestartAltIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  Palette as PaletteIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Tab as TabIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  Code as CodeIcon,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  Link as LinkIcon,
  Image as ImageIcon,
  TableChart,
  FormatColorText,
  FormatColorFill,
  Undo,
  Redo,
  FormatSize,
  FormatQuote,
  CloudUpload,
  InsertLink,
  AspectRatio,
  PhotoSizeSelectActual,
  History,
  AddPhotoAlternate,
  Campaign as CampaignIcon,
} from '@mui/icons-material';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../firebase';
import { useTheme } from '@mui/material/styles';
import { v4 as uuidv4 } from 'uuid';
import { getAboutSettings, saveAboutSettings, getAboutTabs, saveAboutTab, deleteAboutTab } from '../../../services/ecommerceApi';
import { ImageUploadService } from '../../accounting/inventory/services/imageUploadService';
import SectionVisibilityToggle from './shared/SectionVisibilityToggle';
import ColorPalettePicker from './shared/ColorPalettePicker';
import {
  DEFAULT_STATS_BACKGROUND_COLOR,
  DEFAULT_STATS_LABELS,
  DEFAULT_STATS_TEXT_COLOR,
  DEFAULT_VALUES_ACCENT_COLOR,
  DEFAULT_VALUES_CHECKLIST,
  DEFAULT_VALUES_EYEBROW,
  DEFAULT_VALUES_HEADING_COLOR,
  DEFAULT_VALUES_HEADING_FONT_SIZE,
  TEXT_COLOR_PRESETS,
  normalizeAboutValuesTemplate,
  parseSectionSettingsJson,
  resolveAboutCtaSection,
  resolveSectionSettingsFromSource,
  serializeSectionSettingsJson,
  type AboutValuesTemplate,
  type SectionSettingsPayload,
  type StatsLabels,
} from '../utils/corporateSectionSettings';
import AboutValuesTemplatePicker from './AboutValuesTemplatePicker';
import CorporateCtaSettingsPanel from './CorporateCtaSettingsPanel';
import { useTranslation } from 'react-i18next';
import type { CorporateHeroSectionSettings } from '../../../types/corporateHero';
import { defaultCorporateHeroSectionSettings } from '../../../types/corporateHero';
import { normalizeCorporateHeroSection } from '../../../utils/corporateHeroSettings';
import CorporateHeroSettingsPanel from './corporateHero/CorporateHeroSettingsPanel';
import { SortableCustomTabSections } from './customTabs/SortableCustomTabSections';
import { parseTabSections } from './customTabs/parseTabSections';
import HTMLEditor from './HTMLEditor';
import SortableSubTabs from './SortableSubTabs';
import { buildHeroSectionOptions } from '../../../utils/corporateHeroSectionOptions';

// Yeni Sekme Input Component
interface NewTabInputProps {
  onAddTab: (tabName: string) => void;
  onCancel: () => void;
}

const NewTabInput: React.FC<NewTabInputProps> = ({ onAddTab, onCancel }) => {
  const [tabName, setTabName] = useState('');

  const handleSubmit = () => {
    if (tabName.trim()) {
      onAddTab(tabName.trim());
      setTabName('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <Box>
      <TextField
        autoFocus
        margin="dense"
        label="Sekme Adı"
        fullWidth
        variant="outlined"
        value={tabName}
        onChange={(e) => setTabName(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder="Örn: Vizyonumuz, Misyonumuz, Ekibimiz"
      />
      <DialogActions sx={{ px: 0, pt: 2 }}>
        <Button onClick={onCancel}>İptal</Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={!tabName.trim()}
        >
          Ekle
        </Button>
      </DialogActions>
    </Box>
  );
};

// NOT: HTML editörü artık paylaşılan ./HTMLEditor bileşenine taşındı (resim
// ekleme/donma sorunları orada kalıcı olarak düzeltildi). Aşağıdaki eski kopya
// KULLANILMIYOR; JSX içindeki <HTMLEditor> yukarıda import edilen paylaşılan
// sürümü kullanır. Bu eski blok ileride tamamen silinebilir.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface LegacyHTMLEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  companyId?: string | null; // Firebase Storage için
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LegacyHTMLEditor: React.FC<LegacyHTMLEditorProps> = ({ value, onChange, placeholder, disabled, companyId }) => {
  const [isCodeView, setIsCodeView] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showBgColorPicker, setShowBgColorPicker] = useState(false);
  const [showFontSizePicker, setShowFontSizePicker] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imageUploadMode, setImageUploadMode] = useState<'url' | 'upload'>('url');
  const [imageUploading, setImageUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  const [selectedImage, setSelectedImage] = useState<HTMLImageElement | null>(null);
  const [imageResizeDialogOpen, setImageResizeDialogOpen] = useState(false);
  const [imageWidth, setImageWidth] = useState<string>('');
  const [imageHeight, setImageHeight] = useState<string>('');
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [originalAspectRatio, setOriginalAspectRatio] = useState<number>(1);
  const [uploadedImages, setUploadedImages] = useState<Array<{url: string, name: string, uploadDate: string}>>([]);
  const [showUploadedImages, setShowUploadedImages] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  
  const editorRef = React.useRef<HTMLDivElement>(null);
  const colorPickerRef = React.useRef<HTMLDivElement>(null);
  const bgColorPickerRef = React.useRef<HTMLDivElement>(null);
  const fontSizePickerRef = React.useRef<HTMLDivElement>(null);
  const isUserTyping = React.useRef(false); // ✅ Kullanıcı yazıyor mu kontrolü

  const colors = [
    '#000000', '#333333', '#666666', '#999999', '#CCCCCC', '#FFFFFF',
    '#FF0000', '#FF6600', '#FFCC00', '#FFFF00', '#CCFF00', '#66FF00',
    '#00FF00', '#00FF66', '#00FFCC', '#00FFFF', '#00CCFF', '#0066FF',
    '#0000FF', '#6600FF', '#CC00FF', '#FF00FF', '#FF00CC', '#FF0066'
  ];

  const fontSizes = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 60, 72];

  const handleCommand = (command: string, value?: string) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand(command, false, value);
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleContentChange = () => {
    if (editorRef.current) {
      isUserTyping.current = true; // ✅ Kullanıcı yazıyor
      onChange(editorRef.current.innerHTML);
    }
  };

  // ✅ Value dışarıdan değiştiğinde (prop olarak) editörü güncelle
  React.useEffect(() => {
    if (editorRef.current && !isUserTyping.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
    isUserTyping.current = false; // Reset flag
  }, [value]);

  // Gelişmiş içerik hizalama
  const handleContentAlignment = (alignment: 'left' | 'center' | 'right') => {
    if (!editorRef.current) return;

    // Seçili metni al
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    
    // Eğer hiçbir şey seçili değilse, tüm editör içeriğini hizala
    if (selection.toString().trim() === '') {
      // Tüm editör içeriğini div ile sar ve hizala
      const alignmentStyle = alignment === 'left' ? 'text-align: left;' : 
                           alignment === 'center' ? 'text-align: center;' : 
                           'text-align: right;';
      
      const currentContent = editorRef.current.innerHTML;
      const alignedContent = `<div style="${alignmentStyle}">${currentContent}</div>`;
      
      editorRef.current.innerHTML = alignedContent;
      onChange(editorRef.current.innerHTML);
      return;
    }

    // Seçili içeriği hizala
    try {
      // Seçili içeriği div ile sar
      const selectedContent = range.extractContents();
      const div = document.createElement('div');
      
      // Hizalama stilini ekle
      switch (alignment) {
        case 'left':
          div.style.textAlign = 'left';
          break;
        case 'center':
          div.style.textAlign = 'center';
          break;
        case 'right':
          div.style.textAlign = 'right';
          break;
      }
      
      div.appendChild(selectedContent);
      range.insertNode(div);
      
      // Seçimi temizle
      selection.removeAllRanges();
      
      // İçeriği güncelle
      onChange(editorRef.current.innerHTML);
    } catch (error) {
      console.error('Hizalama hatası:', error);
      // Fallback: Standart hizalama komutlarını kullan
      const command = alignment === 'left' ? 'justifyLeft' : 
                     alignment === 'center' ? 'justifyCenter' : 
                     'justifyRight';
      handleCommand(command);
    }
  };

  // Resim seçme ve boyutlandırma
  const handleImageClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.tagName === 'IMG') {
      const img = target as HTMLImageElement;
      setSelectedImage(img);
      
      // Mevcut boyutları al
      const currentWidth = img.style.width || img.width + 'px';
      const currentHeight = img.style.height || img.height + 'px';
      
      setImageWidth(currentWidth.replace('px', ''));
      setImageHeight(currentHeight.replace('px', ''));
      
      // Orijinal aspect ratio'yu hesapla
      const naturalWidth = img.naturalWidth || img.width;
      const naturalHeight = img.naturalHeight || img.height;
      setOriginalAspectRatio(naturalWidth / naturalHeight);
      
      setImageResizeDialogOpen(true);
    }
  };

  const applyImageResize = () => {
    if (!selectedImage) return;

    const widthValue = imageWidth ? `${imageWidth}px` : 'auto';
    const heightValue = imageHeight ? `${imageHeight}px` : 'auto';

    selectedImage.style.width = widthValue;
    selectedImage.style.height = heightValue;
    
    // Editör içeriğini güncelle
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }

    // Dialog'u kapat
    setImageResizeDialogOpen(false);
    setSelectedImage(null);
  };

  const handleWidthChange = (newWidth: string) => {
    setImageWidth(newWidth);
    
    if (maintainAspectRatio && newWidth && originalAspectRatio) {
      const calculatedHeight = Math.round(parseInt(newWidth) / originalAspectRatio);
      setImageHeight(calculatedHeight.toString());
    }
  };

  const handleHeightChange = (newHeight: string) => {
    setImageHeight(newHeight);
    
    if (maintainAspectRatio && newHeight && originalAspectRatio) {
      const calculatedWidth = Math.round(parseInt(newHeight) * originalAspectRatio);
      setImageWidth(calculatedWidth.toString());
    }
  };

  const resetImageSize = () => {
    if (!selectedImage) return;
    
    const naturalWidth = selectedImage.naturalWidth;
    const naturalHeight = selectedImage.naturalHeight;
    
    setImageWidth(naturalWidth.toString());
    setImageHeight(naturalHeight.toString());
  };

  const setImageSizePreset = (preset: 'small' | 'medium' | 'large' | 'full') => {
    if (!selectedImage) return;
    
    const naturalWidth = selectedImage.naturalWidth;
    const naturalHeight = selectedImage.naturalHeight;
    
    let newWidth: number;
    
    switch (preset) {
      case 'small':
        newWidth = Math.min(200, naturalWidth);
        break;
      case 'medium':
        newWidth = Math.min(400, naturalWidth);
        break;
      case 'large':
        newWidth = Math.min(600, naturalWidth);
        break;
      case 'full':
        newWidth = naturalWidth;
        break;
      default:
        newWidth = naturalWidth;
    }
    
    const newHeight = Math.round(newWidth / originalAspectRatio);
    
    setImageWidth(newWidth.toString());
    setImageHeight(newHeight.toString());
  };

  const handleCodeChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(event.target.value);
  };

  const insertLink = () => {
    if (linkUrl && linkText) {
      const linkHtml = `<a href="${linkUrl}" target="_blank">${linkText}</a>`;
      handleCommand('insertHTML', linkHtml);
      setLinkUrl('');
      setLinkText('');
      setLinkDialogOpen(false);
    }
  };

  const insertImage = async () => {
    try {
      setImageUploading(true);
      let imagesHtml = '';

      // URL modu
      if (imageUploadMode === 'url' && imageUrl) {
        imagesHtml = `<img src="${imageUrl}" alt="${imageAlt}" style="max-width: 100%; height: auto; margin: 8px;" />`;
      }
      
      // Dosya yükleme modu - birden fazla dosya
      else if (imageUploadMode === 'upload' && imageFiles.length > 0 && companyId) {
        const uploadPromises = imageFiles.map(async (file, index) => {
          try {
            // Progress tracking için unique key
            const progressKey = `${file.name}-${index}`;
            setUploadProgress(prev => ({ ...prev, [progressKey]: 0 }));
            
            // Dosya adını oluştur
            const fileExtension = file.name.split('.').pop();
            const fileName = `about-content-${Date.now()}-${index}-${uuidv4()}.${fileExtension}`;
            
            // Firebase Storage referansı
            const imageRef = storageRef(storage, `companies/${companyId}/about-images/${fileName}`);
            
            // Dosyayı yükle
            await uploadBytes(imageRef, file);
            
            // Progress güncelle
            setUploadProgress(prev => ({ ...prev, [progressKey]: 100 }));
            
            // Download URL'ini al
            const downloadURL = await getDownloadURL(imageRef);
            
            // Yüklenen resmi listeye ekle
            const uploadedImage = {
              url: downloadURL,
              name: file.name,
              uploadDate: new Date().toISOString()
            };
            
            setUploadedImages(prev => {
              const exists = prev.some(img => img.url === downloadURL);
              if (!exists) {
                return [...prev, uploadedImage];
              }
              return prev;
            });
            
            return `<img src="${downloadURL}" alt="${imageAlt || file.name}" style="max-width: 100%; height: auto; margin: 8px;" />`;
          } catch (error) {
            console.error(`${file.name} yüklenirken hata:`, error);
            return `<p style="color: red;">❌ ${file.name} yüklenemedi</p>`;
          }
        });

        const uploadedImages = await Promise.all(uploadPromises);
        imagesHtml = uploadedImages.join('\n');
      }

      if (imagesHtml) {
        handleCommand('insertHTML', imagesHtml);
        
        // Form'u temizle
        setImageUrl('');
        setImageAlt('');
        setImageFiles([]);
        setImageUploadMode('url');
        setUploadProgress({});
        setImageDialogOpen(false);
      }
    } catch (error) {
      console.error('Resimler yüklenirken hata:', error);
      alert('Resimler yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setImageUploading(false);
    }
  };

  const handleImageFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    if (files.length === 0) return;

    const validFiles: File[] = [];
    const errors: string[] = [];

    files.forEach((file, index) => {
      // Dosya boyutu kontrolü (5MB)
      if (file.size > 5 * 1024 * 1024) {
        errors.push(`${file.name}: Dosya boyutu 5MB'dan küçük olmalıdır.`);
        return;
      }
      
      // Dosya tipi kontrolü
      if (!file.type.startsWith('image/')) {
        errors.push(`${file.name}: Sadece resim dosyası kabul edilir.`);
        return;
      }

      validFiles.push(file);
    });

    // Hataları göster
    if (errors.length > 0) {
      alert('Bazı dosyalar yüklenemedi:\n\n' + errors.join('\n'));
    }

    // Geçerli dosyaları ekle (mevcut dosyalarla birleştir)
    if (validFiles.length > 0) {
      setImageFiles(prev => [...prev, ...validFiles]);
    }

    // Input'u temizle (aynı dosyayı tekrar seçebilmek için)
    event.target.value = '';
  };

  const removeImageFile = (index: number) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearAllImageFiles = () => {
    setImageFiles([]);
    setUploadProgress({});
  };

  const insertUploadedImage = (imageUrl: string, imageName: string) => {
    const imgHtml = `<img src="${imageUrl}" alt="${imageAlt || imageName}" style="max-width: 100%; height: auto; margin: 8px;" />`;
    handleCommand('insertHTML', imgHtml);
    
    // Modal'ı kapat
    setImageUrl('');
    setImageAlt('');
    setImageFiles([]);
    setImageUploadMode('url');
    setUploadProgress({});
    setImageDialogOpen(false);
  };

  const deleteUploadedImage = (imageUrl: string) => {
    setUploadedImages(prev => prev.filter(img => img.url !== imageUrl));
  };

  const insertTable = () => {
    let tableHtml = '<table border="1" style="border-collapse: collapse; width: 100%;">';
    for (let i = 0; i < tableRows; i++) {
      tableHtml += '<tr>';
      for (let j = 0; j < tableCols; j++) {
        tableHtml += '<td style="padding: 8px; border: 1px solid #ddd;">Hücre</td>';
      }
      tableHtml += '</tr>';
    }
    tableHtml += '</table>';
    handleCommand('insertHTML', tableHtml);
    setTableDialogOpen(false);
  };

  const handleColorSelect = (color: string) => {
    if (!editorRef.current) return;
    
    // Seçili metni al
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      // Hiçbir şey seçili değilse, editörün tamamına uygula
      editorRef.current.style.color = color;
    } else {
      // Seçili metin varsa, sadece o kısma uygula
      const range = selection.getRangeAt(0);
      if (range.collapsed) {
        // İmleç bir yerdeyse, yeni yazılacak metne uygula
        document.execCommand('foreColor', false, color);
      } else {
        // Metin seçiliyse, span ile sarala
        const span = document.createElement('span');
        span.style.color = color;
        try {
          range.surroundContents(span);
        } catch (e) {
          // Karmaşık seçimler için alternatif yöntem
          const contents = range.extractContents();
          span.appendChild(contents);
          range.insertNode(span);
        }
      }
    }
    
    onChange(editorRef.current.innerHTML);
    setShowColorPicker(false);
  };

  const handleBgColorSelect = (color: string) => {
    if (!editorRef.current) return;
    
    // Seçili metni al
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      // Hiçbir şey seçili değilse, editörün tamamına uygula
      editorRef.current.style.backgroundColor = color;
    } else {
      // Seçili metin varsa, sadece o kısma uygula
      const range = selection.getRangeAt(0);
      if (range.collapsed) {
        // İmleç bir yerdeyse, yeni yazılacak metne uygula
        document.execCommand('backColor', false, color);
      } else {
        // Metin seçiliyse, span ile sarala
        const span = document.createElement('span');
        span.style.backgroundColor = color;
        try {
          range.surroundContents(span);
        } catch (e) {
          // Karmaşık seçimler için alternatif yöntem
          const contents = range.extractContents();
          span.appendChild(contents);
          range.insertNode(span);
        }
      }
    }
    
    onChange(editorRef.current.innerHTML);
    setShowBgColorPicker(false);
  };

  const handleFontSizeSelect = (size: number) => {
    if (!editorRef.current) return;
    
    // Seçili metni al
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      // Hiçbir şey seçili değilse, editörün tamamına uygula
      editorRef.current.style.fontSize = `${size}px`;
    } else {
      // Seçili metin varsa, sadece o kısma uygula
      const range = selection.getRangeAt(0);
      if (range.collapsed) {
        // İmleç bir yerdeyse, yeni yazılacak metne uygula
        document.execCommand('fontSize', false, '7'); // Geçici değer
        const fontElements = editorRef.current.querySelectorAll('font[size="7"]');
        fontElements.forEach(el => {
          el.removeAttribute('size');
          (el as HTMLElement).style.fontSize = `${size}px`;
        });
      } else {
        // Metin seçiliyse, span ile sarala
        const span = document.createElement('span');
        span.style.fontSize = `${size}px`;
        try {
          range.surroundContents(span);
        } catch (e) {
          // Karmaşık seçimler için alternatif yöntem
          const contents = range.extractContents();
          span.appendChild(contents);
          range.insertNode(span);
        }
      }
    }
    
    onChange(editorRef.current.innerHTML);
    setShowFontSizePicker(false);
  };

  // Click outside handlers
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
        setShowColorPicker(false);
      }
      if (bgColorPickerRef.current && !bgColorPickerRef.current.contains(event.target as Node)) {
        setShowBgColorPicker(false);
      }
      if (fontSizePickerRef.current && !fontSizePickerRef.current.contains(event.target as Node)) {
        setShowFontSizePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Box sx={{ border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden', position: 'relative' }}>
      {/* Toolbar */}
      <Box sx={{ 
        display: 'flex', 
        gap: 0.5, 
        p: 1, 
        borderBottom: '1px solid #ddd', 
        backgroundColor: '#f8f9fa',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Geri Al / İleri Al */}
        <Tooltip title="Geri Al">
          <IconButton size="small" onClick={() => handleCommand('undo')} disabled={disabled || isCodeView}>
            <Undo />
          </IconButton>
        </Tooltip>
        <Tooltip title="İleri Al">
          <IconButton size="small" onClick={() => handleCommand('redo')} disabled={disabled || isCodeView}>
            <Redo />
          </IconButton>
        </Tooltip>
        
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Font Boyutu */}
        <Box sx={{ position: 'relative' }} ref={fontSizePickerRef}>
          <Tooltip title="Font Boyutu">
            <IconButton 
              size="small" 
              onClick={() => setShowFontSizePicker(!showFontSizePicker)}
              disabled={disabled || isCodeView}
            >
              <FormatSize />
            </IconButton>
          </Tooltip>
          {showFontSizePicker && (
            <Paper sx={{ 
              position: 'absolute', 
              top: '100%', 
              left: 0, 
              zIndex: 1000,
              maxHeight: 200,
              overflow: 'auto',
              minWidth: 80
            }}>
              {fontSizes.map(size => (
                <Button
                  key={size}
                  fullWidth
                  size="small"
                  onClick={() => handleFontSizeSelect(size)}
                  sx={{ justifyContent: 'flex-start', fontSize: `${Math.min(size, 16)}px` }}
                >
                  {size}px
                </Button>
              ))}
            </Paper>
          )}
        </Box>

        {/* Metin Formatı */}
        <Tooltip title="Kalın">
          <IconButton size="small" onClick={() => handleCommand('bold')} disabled={disabled || isCodeView}>
            <FormatBold />
          </IconButton>
        </Tooltip>
        <Tooltip title="İtalik">
          <IconButton size="small" onClick={() => handleCommand('italic')} disabled={disabled || isCodeView}>
            <FormatItalic />
          </IconButton>
        </Tooltip>
        <Tooltip title="Altı Çizili">
          <IconButton size="small" onClick={() => handleCommand('underline')} disabled={disabled || isCodeView}>
            <FormatUnderlined />
          </IconButton>
        </Tooltip>

        {/* Renk Seçiciler */}
        <Box sx={{ position: 'relative' }} ref={colorPickerRef}>
          <Tooltip title="Metin Rengi">
            <IconButton 
              size="small" 
              onClick={() => setShowColorPicker(!showColorPicker)}
              disabled={disabled || isCodeView}
            >
              <FormatColorText />
            </IconButton>
          </Tooltip>
          {showColorPicker && (
            <Paper sx={{ 
              position: 'absolute', 
              top: '100%', 
              left: 0, 
              zIndex: 1000,
              p: 1,
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',
              gap: 0.5,
              width: 180
            }}>
              {colors.map(color => (
                <Box
                  key={color}
                  onClick={() => handleColorSelect(color)}
                  sx={{
                    width: 24,
                    height: 24,
                    backgroundColor: color,
                    border: '1px solid #ccc',
                    cursor: 'pointer',
                    '&:hover': { transform: 'scale(1.1)' }
                  }}
                />
              ))}
            </Paper>
          )}
        </Box>

        <Box sx={{ position: 'relative' }} ref={bgColorPickerRef}>
          <Tooltip title="Arka Plan Rengi">
            <IconButton 
              size="small" 
              onClick={() => setShowBgColorPicker(!showBgColorPicker)}
              disabled={disabled || isCodeView}
            >
              <FormatColorFill />
            </IconButton>
          </Tooltip>
          {showBgColorPicker && (
            <Paper sx={{ 
              position: 'absolute', 
              top: '100%', 
              left: 0, 
              zIndex: 1000,
              p: 1,
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',
              gap: 0.5,
              width: 180
            }}>
              {colors.map(color => (
                <Box
                  key={color}
                  onClick={() => handleBgColorSelect(color)}
                  sx={{
                    width: 24,
                    height: 24,
                    backgroundColor: color,
                    border: '1px solid #ccc',
                    cursor: 'pointer',
                    '&:hover': { transform: 'scale(1.1)' }
                  }}
                />
              ))}
            </Paper>
          )}
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Hizalama - Gelişmiş */}
        <Box sx={{ display: 'flex', border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
          <Tooltip title="Sola Hizala">
            <IconButton 
              size="small" 
              onClick={() => handleContentAlignment('left')} 
              disabled={disabled || isCodeView}
              sx={{ 
                borderRadius: 0,
                borderRight: '1px solid #ddd',
                '&:hover': { backgroundColor: '#e3f2fd' }
              }}
            >
              <FormatAlignLeft />
            </IconButton>
          </Tooltip>
          <Tooltip title="Ortala">
            <IconButton 
              size="small" 
              onClick={() => handleContentAlignment('center')} 
              disabled={disabled || isCodeView}
              sx={{ 
                borderRadius: 0,
                borderRight: '1px solid #ddd',
                '&:hover': { backgroundColor: '#e3f2fd' }
              }}
            >
              <FormatAlignCenter />
            </IconButton>
          </Tooltip>
          <Tooltip title="Sağa Hizala">
            <IconButton 
              size="small" 
              onClick={() => handleContentAlignment('right')} 
              disabled={disabled || isCodeView}
              sx={{ 
                borderRadius: 0,
                '&:hover': { backgroundColor: '#e3f2fd' }
              }}
            >
              <FormatAlignRight />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Ek Hizalama Seçenekleri */}
        <Tooltip title="İki Yana Yasla">
          <IconButton size="small" onClick={() => handleCommand('justifyFull')} disabled={disabled || isCodeView}>
            <FormatAlignJustify />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Listeler */}
        <Tooltip title="Madde İşaretli Liste">
          <IconButton size="small" onClick={() => handleCommand('insertUnorderedList')} disabled={disabled || isCodeView}>
            <FormatListBulleted />
          </IconButton>
        </Tooltip>
        <Tooltip title="Numaralı Liste">
          <IconButton size="small" onClick={() => handleCommand('insertOrderedList')} disabled={disabled || isCodeView}>
            <FormatListNumbered />
          </IconButton>
        </Tooltip>

        {/* Alıntı */}
        <Tooltip title="Alıntı">
          <IconButton size="small" onClick={() => handleCommand('formatBlock', '<blockquote>')} disabled={disabled || isCodeView}>
            <FormatQuote />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Başlıklar */}
        <Tooltip title="Başlık 1">
          <Button size="small" onClick={() => handleCommand('formatBlock', '<h1>')} disabled={disabled || isCodeView} sx={{ minWidth: 'auto', px: 1, fontSize: '12px' }}>
            H1
          </Button>
        </Tooltip>
        <Tooltip title="Başlık 2">
          <Button size="small" onClick={() => handleCommand('formatBlock', '<h2>')} disabled={disabled || isCodeView} sx={{ minWidth: 'auto', px: 1, fontSize: '12px' }}>
            H2
          </Button>
        </Tooltip>
        <Tooltip title="Başlık 3">
          <Button size="small" onClick={() => handleCommand('formatBlock', '<h3>')} disabled={disabled || isCodeView} sx={{ minWidth: 'auto', px: 1, fontSize: '12px' }}>
            H3
          </Button>
        </Tooltip>
        <Tooltip title="Paragraf">
          <Button size="small" onClick={() => handleCommand('formatBlock', '<p>')} disabled={disabled || isCodeView} sx={{ minWidth: 'auto', px: 1, fontSize: '12px' }}>
            P
          </Button>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Link, Resim, Tablo */}
        <Tooltip title="Link Ekle">
          <IconButton size="small" onClick={() => setLinkDialogOpen(true)} disabled={disabled || isCodeView}>
            <LinkIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Resim Ekle">
          <IconButton size="small" onClick={() => setImageDialogOpen(true)} disabled={disabled || isCodeView}>
            <ImageIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Tablo Ekle">
          <IconButton size="small" onClick={() => setTableDialogOpen(true)} disabled={disabled || isCodeView}>
            <TableChart />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* HTML Kod Görünümü */}
        <Tooltip title={isCodeView ? "Görsel Editör" : "HTML Kodu"}>
          <IconButton 
            size="small" 
            onClick={() => setIsCodeView(!isCodeView)}
            disabled={disabled}
            color={isCodeView ? "primary" : "default"}
          >
            <CodeIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Editor Content */}
      {isCodeView ? (
        <TextField
          multiline
          rows={12}
          value={value}
          onChange={handleCodeChange}
          placeholder={placeholder}
          disabled={disabled}
          variant="standard"
          InputProps={{ disableUnderline: true }}
          sx={{ 
            '& .MuiInputBase-root': { 
              p: 2,
              fontFamily: 'Monaco, Consolas, "Courier New", monospace',
              fontSize: '13px',
              lineHeight: 1.4
            }
          }}
          fullWidth
        />
      ) : (
        <Box
          ref={editorRef}
          contentEditable={!disabled}
          onInput={handleContentChange}
          onClick={handleImageClick}
          suppressContentEditableWarning
          sx={{
            minHeight: '300px',
            p: 2,
            outline: 'none',
            lineHeight: 1.6,
            '&:focus': {
              backgroundColor: '#fafafa'
            },
            '&[contenteditable="false"]': {
              backgroundColor: '#f5f5f5',
              color: '#666'
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              margin: '16px 0 8px 0',
              fontWeight: 'bold'
            },
            '& p': {
              margin: '8px 0'
            },
            '& ul, & ol': {
              margin: '8px 0',
              paddingLeft: '24px'
            },
            '& blockquote': {
              margin: '16px 0',
              padding: '12px 16px',
              borderLeft: '4px solid #ddd',
              backgroundColor: '#f9f9f9',
              fontStyle: 'italic'
            },
            '& table': {
              borderCollapse: 'collapse',
              width: '100%',
              margin: '16px 0'
            },
            '& td, & th': {
              padding: '8px',
              border: '1px solid #ddd'
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              cursor: 'pointer',
              border: '2px solid transparent',
              borderRadius: '4px',
              transition: 'border-color 0.2s ease',
              '&:hover': {
                borderColor: '#1976d2',
                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)'
              }
            }
          }}
        />
      )}
      
      {!value && !isCodeView && (
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            position: 'absolute', 
            top: '80px', 
            left: '16px',
            pointerEvents: 'none',
            opacity: 0.6
          }}
        >
          {placeholder}
        </Typography>
      )}

      {/* Link Dialog */}
      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Link Ekle</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Link Metni"
            fullWidth
            variant="outlined"
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="URL"
            fullWidth
            variant="outlined"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://example.com"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>İptal</Button>
          <Button onClick={insertLink} variant="contained" disabled={!linkUrl || !linkText}>
            Ekle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Dialog */}
      <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImageIcon />
            Resim Ekle
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Mod Seçimi */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Resim Ekleme Yöntemi
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant={imageUploadMode === 'url' ? 'contained' : 'outlined'}
                startIcon={<InsertLink />}
                onClick={() => {
                  setImageUploadMode('url');
                  setShowUploadedImages(false);
                }}
                size="small"
              >
                URL ile Ekle
              </Button>
              <Button
                variant={imageUploadMode === 'upload' ? 'contained' : 'outlined'}
                startIcon={<CloudUpload />}
                onClick={() => {
                  setImageUploadMode('upload');
                  setShowUploadedImages(false);
                }}
                size="small"
                disabled={!companyId}
              >
                Dosya Yükle
              </Button>
              {uploadedImages.length > 0 && (
                <Button
                  variant={showUploadedImages ? 'contained' : 'outlined'}
                  startIcon={<History />}
                  onClick={() => {
                    setShowUploadedImages(!showUploadedImages);
                    if (!showUploadedImages) {
                      setImageUploadMode('url'); // Reset mode
                    }
                  }}
                  size="small"
                  color="secondary"
                >
                  Yüklenen Resimler ({uploadedImages.length})
                </Button>
              )}
            </Box>
            {!companyId && (
              <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                ⚠️ Dosya yükleme için şirket ID gerekli
              </Typography>
            )}
          </Box>

          {/* URL Modu */}
          {imageUploadMode === 'url' && (
            <Box>
              <TextField
                autoFocus
                margin="dense"
                label="Resim URL"
                fullWidth
                variant="outlined"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                sx={{ mb: 2 }}
                helperText="Resmin internet adresini girin"
              />
            </Box>
          )}

          {/* Dosya Yükleme Modu */}
          {imageUploadMode === 'upload' && (
            <Box>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="image-upload-input"
                type="file"
                multiple
                onChange={handleImageFilesChange}
              />
              <label htmlFor="image-upload-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  fullWidth
                  sx={{ 
                    mb: 2, 
                    py: 2,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    '&:hover': {
                      borderStyle: 'dashed',
                      borderWidth: 2,
                    }
                  }}
                >
                  {imageFiles.length > 0 
                    ? `${imageFiles.length} Resim Seçildi - Daha Fazla Ekle` 
                    : 'Resim Dosyalarını Seç (Çoklu Seçim)'}
                </Button>
              </label>
              
              {imageFiles.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Seçilen Resimler ({imageFiles.length})
                    </Typography>
                    <Button 
                      size="small" 
                      color="error" 
                      onClick={clearAllImageFiles}
                      startIcon={<DeleteIcon />}
                    >
                      Tümünü Temizle
                    </Button>
                  </Box>
                  
                  <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                    {imageFiles.map((file, index) => (
                      <Box 
                        key={`${file.name}-${index}`}
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: 1,
                          mb: 1,
                          backgroundColor: '#f9f9f9',
                          borderRadius: 1,
                          '&:last-child': { mb: 0 }
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            📷 {file.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                          {uploadProgress[`${file.name}-${index}`] !== undefined && (
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="primary.main">
                                Yükleniyor: %{uploadProgress[`${file.name}-${index}`]}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <IconButton 
                          size="small" 
                          color="error" 
                          onClick={() => removeImageFile(index)}
                          disabled={imageUploading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                • <strong>Çoklu seçim:</strong> Ctrl/Cmd tuşu ile birden fazla resim seçebilirsiniz<br/>
                • <strong>Maksimum dosya boyutu:</strong> Her dosya için 5MB<br/>
                • <strong>Desteklenen formatlar:</strong> JPG, PNG, GIF, WebP<br/>
                • <strong>Depolama:</strong> Tüm resimler Firebase Storage'a yüklenecek
              </Typography>
            </Box>
          )}

          {/* Yüklenen Resimler Bölümü */}
          {showUploadedImages && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Daha Önce Yüklenen Resimler:
              </Typography>
              
              {uploadedImages.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 4, 
                  border: '2px dashed #ddd', 
                  borderRadius: 1,
                  backgroundColor: '#f9f9f9'
                }}>
                  <AddPhotoAlternate sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Henüz resim yüklenmemiş
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Dosya yükleyerek resimleriniz burada görünecek
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ 
                  maxHeight: 300, 
                  overflow: 'auto', 
                  border: '1px solid #ddd', 
                  borderRadius: 1, 
                  p: 2,
                  backgroundColor: '#fafafa'
                }}>
                  <Grid container spacing={2}>
                    {uploadedImages.map((image, index) => (
                      <Grid item xs={6} sm={4} md={3} key={`${image.url}-${index}`}>
                        <Card sx={{ position: 'relative', '&:hover': { boxShadow: 3 } }}>
                          <Box sx={{ position: 'relative', paddingTop: '75%' }}>
                            <img 
                              src={image.url} 
                              alt={image.name}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '4px 4px 0 0'
                              }}
                            />
                          </Box>
                          <CardContent sx={{ p: 1 }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: '11px'
                              }}
                              title={image.name}
                            >
                              {image.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                              {new Date(image.uploadDate).toLocaleDateString('tr-TR')}
                            </Typography>
                          </CardContent>
                          
                          {/* Hover Butonları */}
                          <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            borderRadius: 1,
                            '&:hover': { opacity: 1 }
                          }}>
                            <Tooltip title="Resmi Ekle">
                              <IconButton
                                size="small"
                                onClick={() => insertUploadedImage(image.url, image.name)}
                                sx={{ 
                                  backgroundColor: 'rgba(255,255,255,0.9)',
                                  color: 'primary.main',
                                  '&:hover': { backgroundColor: 'white' }
                                }}
                              >
                                <AddPhotoAlternate fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Listeden Sil">
                              <IconButton
                                size="small"
                                onClick={() => deleteUploadedImage(image.url)}
                                sx={{ 
                                  backgroundColor: 'rgba(255,255,255,0.9)',
                                  color: 'error.main',
                                  '&:hover': { backgroundColor: 'white' }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
              
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                💡 <strong>İpucu:</strong> Editörden sildiğiniz resimleri buradan tekrar ekleyebilirsiniz. 
                Resmin üzerine gelerek "Resmi Ekle" butonuna tıklayın.
              </Typography>
            </Box>
          )}

          {/* Alt Metin */}
          <TextField
            margin="dense"
            label="Alt Metin (Opsiyonel)"
            fullWidth
            variant="outlined"
            value={imageAlt}
            onChange={(e) => setImageAlt(e.target.value)}
            placeholder="Resim açıklaması"
            helperText="SEO ve erişilebilirlik için önerilir"
          />

          {/* Önizleme */}
          {((imageUploadMode === 'url' && imageUrl) || (imageUploadMode === 'upload' && imageFiles.length > 0)) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Önizleme:
              </Typography>
              <Box sx={{ 
                border: '1px solid #ddd', 
                borderRadius: 1, 
                p: 2,
                backgroundColor: '#fafafa',
                maxHeight: 300,
                overflow: 'auto'
              }}>
                {imageUploadMode === 'url' && imageUrl ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <img 
                      src={imageUrl} 
                      alt={imageAlt || 'Önizleme'} 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '200px', 
                        objectFit: 'contain' 
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </Box>
                ) : imageFiles.length > 0 ? (
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', 
                    gap: 2 
                  }}>
                    {imageFiles.map((file, index) => (
                      <Box 
                        key={`preview-${file.name}-${index}`}
                        sx={{ 
                          position: 'relative',
                          border: '1px solid #ddd',
                          borderRadius: 1,
                          overflow: 'hidden',
                          backgroundColor: '#fff'
                        }}
                      >
                        <img 
                          src={URL.createObjectURL(file)} 
                          alt={file.name} 
                          style={{ 
                            width: '100%', 
                            height: '80px', 
                            objectFit: 'cover' 
                          }}
                        />
                        <Box sx={{ p: 1 }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {file.name}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            backgroundColor: 'rgba(255,255,255,0.8)',
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.9)'
                            }
                          }}
                          onClick={() => removeImageFile(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : null}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setImageDialogOpen(false);
              setImageUrl('');
              setImageAlt('');
              setImageFiles([]);
              setImageUploadMode('url');
              setUploadProgress({});
              setShowUploadedImages(false);
            }}
            disabled={imageUploading}
          >
            İptal
          </Button>
          <Button 
            onClick={insertImage} 
            variant="contained" 
            disabled={
              imageUploading || 
              (imageUploadMode === 'url' && !imageUrl) || 
              (imageUploadMode === 'upload' && imageFiles.length === 0)
            }
            startIcon={imageUploading ? <CircularProgress size={16} /> : <ImageIcon />}
          >
            {imageUploading 
              ? `Yükleniyor... (${imageFiles.length} resim)` 
              : imageUploadMode === 'upload' && imageFiles.length > 1
                ? `${imageFiles.length} Resmi Ekle`
                : 'Resmi Ekle'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Table Dialog */}
      <Dialog open={tableDialogOpen} onClose={() => setTableDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Tablo Ekle</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                label="Satır Sayısı"
                type="number"
                fullWidth
                variant="outlined"
                value={tableRows}
                onChange={(e) => setTableRows(Math.max(1, parseInt(e.target.value) || 1))}
                InputProps={{ inputProps: { min: 1, max: 20 } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Sütun Sayısı"
                type="number"
                fullWidth
                variant="outlined"
                value={tableCols}
                onChange={(e) => setTableCols(Math.max(1, parseInt(e.target.value) || 1))}
                InputProps={{ inputProps: { min: 1, max: 10 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTableDialogOpen(false)}>İptal</Button>
          <Button onClick={insertTable} variant="contained">
            Ekle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resim Boyutlandırma Dialog */}
      <Dialog 
        open={imageResizeDialogOpen} 
        onClose={() => setImageResizeDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhotoSizeSelectActual />
            Resim Boyutunu Ayarla
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Box>
              {/* Mevcut Resim Önizleme */}
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Mevcut Resim:
                </Typography>
                <Box sx={{ 
                  border: '1px solid #ddd', 
                  borderRadius: 1, 
                  p: 2, 
                  backgroundColor: '#fafafa',
                  display: 'inline-block'
                }}>
                  <img 
                    src={selectedImage.src} 
                    alt={selectedImage.alt || 'Seçilen resim'} 
                    style={{ 
                      maxWidth: '200px', 
                      maxHeight: '150px', 
                      objectFit: 'contain' 
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Orijinal Boyut: {selectedImage.naturalWidth} × {selectedImage.naturalHeight} px
                </Typography>
              </Box>

              {/* Hızlı Boyut Seçenekleri */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Hızlı Boyut Seçenekleri:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={() => setImageSizePreset('small')}
                  >
                    Küçük (200px)
                  </Button>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={() => setImageSizePreset('medium')}
                  >
                    Orta (400px)
                  </Button>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={() => setImageSizePreset('large')}
                  >
                    Büyük (600px)
                  </Button>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={() => setImageSizePreset('full')}
                  >
                    Orijinal
                  </Button>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    color="secondary"
                    onClick={resetImageSize}
                  >
                    Sıfırla
                  </Button>
                </Box>
              </Box>

              {/* Manuel Boyut Ayarları */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Manuel Boyut Ayarları:
                </Typography>
                
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={5}>
                    <TextField
                      fullWidth
                      label="Genişlik (px)"
                      type="number"
                      value={imageWidth}
                      onChange={(e) => handleWidthChange(e.target.value)}
                      variant="outlined"
                      size="small"
                      InputProps={{
                        inputProps: { min: 1, max: 2000 }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={2} sx={{ textAlign: 'center' }}>
                    <IconButton
                      onClick={() => setMaintainAspectRatio(!maintainAspectRatio)}
                      color={maintainAspectRatio ? 'primary' : 'default'}
                      title={maintainAspectRatio ? 'Oranı koru (Açık)' : 'Oranı koru (Kapalı)'}
                    >
                      <AspectRatio />
                    </IconButton>
                  </Grid>
                  
                  <Grid item xs={5}>
                    <TextField
                      fullWidth
                      label="Yükseklik (px)"
                      type="number"
                      value={imageHeight}
                      onChange={(e) => handleHeightChange(e.target.value)}
                      variant="outlined"
                      size="small"
                      InputProps={{
                        inputProps: { min: 1, max: 2000 }
                      }}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AspectRatio color={maintainAspectRatio ? 'primary' : 'disabled'} />
                  <Typography variant="body2" color={maintainAspectRatio ? 'primary' : 'text.secondary'}>
                    {maintainAspectRatio ? 'En-boy oranı korunuyor' : 'En-boy oranı serbest'}
                  </Typography>
                </Box>
              </Box>

              {/* Boyut Bilgileri */}
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#f5f5f5', 
                borderRadius: 1,
                mb: 2
              }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  📏 Boyut Bilgileri:
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  • Yeni Boyut: {imageWidth || 'auto'} × {imageHeight || 'auto'} px<br/>
                  • Orijinal Boyut: {selectedImage.naturalWidth} × {selectedImage.naturalHeight} px<br/>
                  • En-Boy Oranı: {originalAspectRatio.toFixed(2)}<br/>
                  • Boyut Değişimi: {imageWidth && selectedImage.naturalWidth ? 
                    `%${Math.round((parseInt(imageWidth) / selectedImage.naturalWidth) * 100)}` : 
                    'Hesaplanıyor...'}
                </Typography>
              </Box>

              {/* Uyarılar */}
              {imageWidth && parseInt(imageWidth) > selectedImage.naturalWidth && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  ⚠️ Resmi orijinal boyutundan büyük yapıyorsunuz. Bu kalite kaybına neden olabilir.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setImageResizeDialogOpen(false)}
          >
            İptal
          </Button>
          <Button 
            onClick={applyImageResize} 
            variant="contained"
            startIcon={<PhotoSizeSelectActual />}
            disabled={!imageWidth && !imageHeight}
          >
            Boyutu Uygula
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Styled components - ProductSettings.tsx'den kopyalandı
const StyledTabs = styled(Tabs)({
  minHeight: 'auto',
  '& .MuiTabs-indicator': {
    display: 'none',
  },
});

const StyledTab = styled((props: { label: string }) => (
    <Tab disableRipple {...props} />
  ))(({ theme }) => ({
    textTransform: 'none',
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(14),
    padding: theme.spacing(1, 3.5),
    minHeight: 'auto',
    minWidth: '120px',
    color: theme.palette.text.secondary,
    position: 'relative',
    backgroundColor: theme.palette.grey[200],
    clipPath: 'polygon(15% 0, 85% 0, 100% 100%, 0% 100%)',
    marginRight: theme.spacing(-2),

    '&:hover': {
        backgroundColor: theme.palette.grey[300],
    },
  
    '&.Mui-selected': {
      color: theme.palette.text.primary,
      fontWeight: theme.typography.fontWeightMedium,
      backgroundColor: theme.palette.background.paper,
      zIndex: 2,
    },
}));

// Tab paneli için yardımcı bileşen
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`about-tabpanel-${index}`}
      aria-labelledby={`about-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface AboutTabSection {
  id: string;
  title: string;
  content: string;
  image?: string;
  imagePosition?: 'left' | 'right';
  imageSize?: 'small' | 'medium' | 'large' | 'xlarge' | 'equal';
  imageWidth?: number | string;
  imageHeight?: number | string;
  imageAnimation?: 'fade' | 'zoom' | 'slide' | 'none';
}

interface AboutTab {
  id: string;
  name: string;
  title: string;
  content: string;
  backgroundColor: string;
  order: number;
  active: boolean;
  sections?: AboutTabSection[]; // Yeni: Dinamik bölümler
  navDisplayMode?: 'panel' | 'navbar' | 'both'; // Gösterim yeri: yan panel / üst navbar / ikisi
}

interface AboutSettings {
  heroSection: CorporateHeroSectionSettings;
  storySection: {
    title: string;
    description: string;
    backgroundColor: string;
    stats: {
      customers: string;
      products: string;
      support: string;
      satisfaction: string;
    };
  };
  valuesSection: {
    title: string;
    description: string;
    backgroundColor: string;
    values: Array<{
      id: string;
      title: string;
      description: string;
      icon: string;
      color: string;
    }>;
  };
  whyChooseSection: {
    title: string;
    description: string;
    backgroundColor: string;
    features: Array<{
      id: string;
      title: string;
      description: string;
    }>;
  };
  customTabs?: AboutTab[]; // Yeni eklenen özel sekmeler
  sectionSettings: SectionSettingsPayload;
}

const ABOUT_VALUE_ICONS: Array<{ value: string; label: string }> = [
  { value: 'heart', label: 'Kalp' },
  { value: 'award', label: 'Ödül' },
  { value: 'shield', label: 'Kalkan' },
  { value: 'target', label: 'Hedef' },
  { value: 'truck', label: 'Teslimat' },
  { value: 'users', label: 'Ekip' },
  { value: 'star', label: 'Yıldız' },
  { value: 'rocket', label: 'Roket' },
  { value: 'lightbulb', label: 'Fikir' },
  { value: 'verified', label: 'Onay' },
  { value: 'speed', label: 'Hız' },
  { value: 'support', label: 'Destek' },
  { value: 'diamond', label: 'Kalite' },
  { value: 'home', label: 'Ev' },
  { value: 'business', label: 'İş' },
  { value: 'leaf', label: 'Yaprak' },
  { value: 'thermometer', label: 'Termometre' },
  { value: 'shield-plus', label: 'Kalkan +' },
];

const defaultAboutSettings: AboutSettings = {
  heroSection: {
    ...defaultCorporateHeroSectionSettings,
    title: 'Hakkımızda',
    description: 'Müşteri memnuniyetini ön planda tutarak kaliteli ürünler ve güvenilir hizmet sunuyoruz.',
    backgroundColor: '#f0f8ff',
    height: 500,
  },
  storySection: {
    title: 'Hikayemiz',
    // Boş bırakılabilir — silinen metin varsayılanla tekrar doldurulmaz
    description: '',
    backgroundColor: '#ffffff',
    stats: {
      customers: '1000+',
      products: '500+',
      support: '24/7',
      satisfaction: '99%'
    }
  },
  valuesSection: {
    title: 'Değerlerimiz',
    description: 'Başarımızın arkasındaki temel değerler ve ilkeler',
    backgroundColor: '#f9f9f9',
    values: [
      {
        id: 'customer-focus',
        title: 'Müşteri Odaklılık',
        description: 'Müşterilerimizin ihtiyaçlarını anlamak ve beklentilerini aşmak için sürekli çalışıyoruz.',
        icon: 'heart',
        color: '#3b82f6'
      },
      {
        id: 'quality',
        title: 'Kalite',
        description: 'Sunduğumuz her ürün ve hizmette en yüksek kalite standartlarını koruyoruz.',
        icon: 'award',
        color: '#10b981'
      },
      {
        id: 'reliability',
        title: 'Güvenilirlik',
        description: 'Müşterilerimizin güvenini kazanmak ve korumak için şeffaf ve dürüst davranıyoruz.',
        icon: 'shield',
        color: '#8b5cf6'
      },
      {
        id: 'innovation',
        title: 'İnovasyon',
        description: 'Teknolojinin gücünü kullanarak sürekli gelişim ve yenilik peşindeyiz.',
        icon: 'target',
        color: '#f59e0b'
      },
      {
        id: 'fast-delivery',
        title: 'Hızlı Teslimat',
        description: 'Siparişlerinizi en kısa sürede ve güvenli şekilde adresinize ulaştırıyoruz.',
        icon: 'truck',
        color: '#ef4444'
      },
      {
        id: 'teamwork',
        title: 'Takım Ruhu',
        description: 'Güçlü ekip çalışması ile müşterilerimize en iyi hizmeti sunuyoruz.',
        icon: 'users',
        color: '#6366f1'
      }
    ]
  },
  whyChooseSection: {
    title: 'Neden Bizi Tercih Etmelisiniz?',
    description: 'Size en iyi alışveriş deneyimini sunmak için sunduğumuz avantajlar',
    backgroundColor: '#ffffff',
    features: [
      {
        id: 'secure-shopping',
        title: 'Güvenli Alışveriş',
        description: 'SSL sertifikası ve güvenli ödeme sistemleri ile alışverişiniz tamamen güvende.'
      },
      {
        id: 'fast-shipping',
        title: 'Hızlı Kargo',
        description: 'Siparişleriniz aynı gün kargoya verilir, 1-3 iş günü içinde elinizde.'
      },
      {
        id: 'easy-return',
        title: 'Kolay İade',
        description: '14 gün içinde koşulsuz iade hakkı, sorunsuz iade süreci.'
      },
      {
        id: 'support',
        title: '7/24 Destek',
        description: 'Müşteri hizmetleri ekibimiz her zaman yanınızda, anında destek.'
      },
      {
        id: 'affordable-prices',
        title: 'Uygun Fiyatlar',
        description: 'Kaliteli ürünleri en uygun fiyatlarla, özel indirimler ve kampanyalar.'
      },
      {
        id: 'wide-range',
        title: 'Geniş Ürün Yelpazesi',
        description: 'Binlerce ürün seçeneği ile aradığınız her şeyi tek yerden bulabilirsiniz.'
      }
    ]
  },
  customTabs: [],
  sectionSettings: {},
};

interface AboutSettingsComponentProps {
  companyId: string | null;
  configId?: string | null; // ✅ YENİ: SQL için configId
  onSettingsChange?: (settings: AboutSettings) => void;
}

const previewValueColumns = (count: number): string => {
  if (count <= 1) return '1fr';
  if (count === 2 || count === 4) return '1fr 1fr';
  if (count === 5) return 'repeat(6, 1fr)';
  return '1fr 1fr 1fr';
};

const previewValueSpan = (count: number, index: number): string | undefined => {
  if (count !== 5) return undefined;
  return index >= 3 ? 'span 3' : 'span 2';
};

const previewValueBorderLeft = (count: number, index: number): string => {
  if (count <= 1) return 'none';
  if (count === 5) return index === 0 || index === 3 ? 'none' : '1px solid #e2e8f0';
  const perRow = count === 2 || count === 4 ? 2 : 3;
  return index % perRow === 0 ? 'none' : '1px solid #e2e8f0';
};

const previewValueBorderTop = (count: number, index: number): string => {
  if (count === 5) return index >= 3 ? '1px solid #e2e8f0' : 'none';
  const perRow = count <= 1 ? 1 : count === 2 || count === 4 ? 2 : 3;
  return index >= perRow ? '1px solid #e2e8f0' : 'none';
};

const AboutSettingsComponent: React.FC<AboutSettingsComponentProps> = ({ 
  companyId,
  configId, // ✅ YENİ: SQL için configId
  onSettingsChange 
}) => {
  const { t } = useTranslation('salesSystem');
  const [settings, setSettings] = useState<AboutSettings>(defaultAboutSettings);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [tabToDelete, setTabToDelete] = useState<string | null>(null);
  const [deleteTabName, setDeleteTabName] = useState<string>('');
  const [deleting, setDeleting] = useState(false);
  const [reorderingTabs, setReorderingTabs] = useState(false);
  const [reorderError, setReorderError] = useState<string | null>(null);
  const [storyImageUploading, setStoryImageUploading] = useState(false);
  const theme = useTheme();

  // Ayarları yükle - SQL'den
  useEffect(() => {
    const loadSettings = async () => {
      if (!companyId || !configId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // ✅ SQL API'den yükle (externalSettings kullanma, her zaman SQL'den yükle)
        // 🔒 MULTI-TENANT: companyId parametresi eklendi
        const response = await getAboutSettings(companyId, configId);

        if (response.success && response.data) {
          // SQL'den gelen veriyi AboutSettings formatına dönüştür
          const sqlData = response.data;
          const loadedSettings: AboutSettings = {
            heroSection: normalizeCorporateHeroSection(sqlData, {
              title: sqlData.heroTitle || defaultAboutSettings.heroSection.title,
              description: sqlData.heroDescription ?? defaultAboutSettings.heroSection.description,
              backgroundColor: sqlData.heroBackgroundColor || defaultAboutSettings.heroSection.backgroundColor,
              height: sqlData.heroHeight || defaultAboutSettings.heroSection.height,
            }),
            storySection: {
              title: sqlData.storyTitle || defaultAboutSettings.storySection.title,
              description: sqlData.storyDescription ?? '',
              backgroundColor: sqlData.storyBackgroundColor || defaultAboutSettings.storySection.backgroundColor,
              stats: {
                customers: sqlData.storyStatCustomers || defaultAboutSettings.storySection.stats.customers,
                products: sqlData.storyStatProducts || defaultAboutSettings.storySection.stats.products,
                support: sqlData.storyStatSupport || defaultAboutSettings.storySection.stats.support,
                satisfaction: sqlData.storyStatSatisfaction || defaultAboutSettings.storySection.stats.satisfaction
              }
            },
            valuesSection: {
              title: sqlData.valuesTitle || defaultAboutSettings.valuesSection.title,
              description: sqlData.valuesDescription || defaultAboutSettings.valuesSection.description,
              backgroundColor: sqlData.valuesBackgroundColor || defaultAboutSettings.valuesSection.backgroundColor,
              values: [
                {
                  id: 'customer-focus',
                  title: sqlData.value1Title || defaultAboutSettings.valuesSection.values[0].title,
                  description: sqlData.value1Description || defaultAboutSettings.valuesSection.values[0].description,
                  icon: sqlData.value1Icon || defaultAboutSettings.valuesSection.values[0].icon,
                  color: sqlData.value1Color || defaultAboutSettings.valuesSection.values[0].color
                },
                {
                  id: 'quality',
                  title: sqlData.value2Title || defaultAboutSettings.valuesSection.values[1].title,
                  description: sqlData.value2Description || defaultAboutSettings.valuesSection.values[1].description,
                  icon: sqlData.value2Icon || defaultAboutSettings.valuesSection.values[1].icon,
                  color: sqlData.value2Color || defaultAboutSettings.valuesSection.values[1].color
                },
                {
                  id: 'reliability',
                  title: sqlData.value3Title || defaultAboutSettings.valuesSection.values[2].title,
                  description: sqlData.value3Description || defaultAboutSettings.valuesSection.values[2].description,
                  icon: sqlData.value3Icon || defaultAboutSettings.valuesSection.values[2].icon,
                  color: sqlData.value3Color || defaultAboutSettings.valuesSection.values[2].color
                },
                {
                  id: 'innovation',
                  title: sqlData.value4Title || defaultAboutSettings.valuesSection.values[3].title,
                  description: sqlData.value4Description || defaultAboutSettings.valuesSection.values[3].description,
                  icon: sqlData.value4Icon || defaultAboutSettings.valuesSection.values[3].icon,
                  color: sqlData.value4Color || defaultAboutSettings.valuesSection.values[3].color
                },
                {
                  id: 'fast-delivery',
                  title: sqlData.value5Title || defaultAboutSettings.valuesSection.values[4].title,
                  description: sqlData.value5Description || defaultAboutSettings.valuesSection.values[4].description,
                  icon: sqlData.value5Icon || defaultAboutSettings.valuesSection.values[4].icon,
                  color: sqlData.value5Color || defaultAboutSettings.valuesSection.values[4].color
                },
                {
                  id: 'teamwork',
                  title: sqlData.value6Title || defaultAboutSettings.valuesSection.values[5].title,
                  description: sqlData.value6Description || defaultAboutSettings.valuesSection.values[5].description,
                  icon: sqlData.value6Icon || defaultAboutSettings.valuesSection.values[5].icon,
                  color: sqlData.value6Color || defaultAboutSettings.valuesSection.values[5].color
                }
              ]
            },
            whyChooseSection: {
              title: sqlData.whyChooseTitle || defaultAboutSettings.whyChooseSection.title,
              description: sqlData.whyChooseDescription || defaultAboutSettings.whyChooseSection.description,
              backgroundColor: sqlData.whyChooseBackgroundColor || defaultAboutSettings.whyChooseSection.backgroundColor,
              features: [
                {
                  id: 'secure-shopping',
                  title: sqlData.feature1Title || defaultAboutSettings.whyChooseSection.features[0].title,
                  description: sqlData.feature1Description || defaultAboutSettings.whyChooseSection.features[0].description
                },
                {
                  id: 'fast-shipping',
                  title: sqlData.feature2Title || defaultAboutSettings.whyChooseSection.features[1].title,
                  description: sqlData.feature2Description || defaultAboutSettings.whyChooseSection.features[1].description
                },
                {
                  id: 'easy-return',
                  title: sqlData.feature3Title || defaultAboutSettings.whyChooseSection.features[2].title,
                  description: sqlData.feature3Description || defaultAboutSettings.whyChooseSection.features[2].description
                },
                {
                  id: 'support',
                  title: sqlData.feature4Title || defaultAboutSettings.whyChooseSection.features[3].title,
                  description: sqlData.feature4Description || defaultAboutSettings.whyChooseSection.features[3].description
                },
                {
                  id: 'affordable-prices',
                  title: sqlData.feature5Title || defaultAboutSettings.whyChooseSection.features[4].title,
                  description: sqlData.feature5Description || defaultAboutSettings.whyChooseSection.features[4].description
                },
                {
                  id: 'wide-range',
                  title: sqlData.feature6Title || defaultAboutSettings.whyChooseSection.features[5].title,
                  description: sqlData.feature6Description || defaultAboutSettings.whyChooseSection.features[5].description
                }
              ]
            },
            sectionSettings: resolveSectionSettingsFromSource(sqlData),
            customTabs: [] // Aşağıda SQL'den yüklenecek
          };

          const savedValueItems = loadedSettings.sectionSettings?.valuesSection?.items;
          if (Array.isArray(savedValueItems)) {
            loadedSettings.valuesSection.values = savedValueItems
              .filter((item) => String(item?.title || '').trim())
              .slice(0, 6)
              .map((item, index) => ({
                id: item.id || `value-${index}`,
                title: item.title || '',
                description: item.description || '',
                icon: item.icon || 'star',
                color: item.color || '#64748b',
              }));
          } else {
            const clearedSlots = [
              sqlData.value1Title,
              sqlData.value2Title,
              sqlData.value3Title,
              sqlData.value4Title,
              sqlData.value5Title,
              sqlData.value6Title,
            ].map((title) => typeof title === 'string' && !title.trim());
            loadedSettings.valuesSection.values = loadedSettings.valuesSection.values.filter((_, index) => !clearedSlots[index]);
          }

          // ✅ YENİ: Custom Tabs'ı SQL'den yükle (EcommerceConfigPages)
          try {
            // 🔒 MULTI-TENANT: companyId parametresi eklendi
            const tabsResponse = await getAboutTabs(configId, 'AboutTab');
            if (tabsResponse.success && tabsResponse.data && Array.isArray(tabsResponse.data)) {
              console.log('✅ [ABOUT TABS] SQL den', tabsResponse.data.length, 'sekme yüklendi');
              
              // SQL'den gelen verileri AboutTab formatına dönüştür
              const customTabs = tabsResponse.data.map((sqlTab: any) => ({
                id: sqlTab.id,
                name: sqlTab.name || sqlTab.title,
                title: sqlTab.title,
                content: sqlTab.content || '',
                backgroundColor: sqlTab.backgroundColor || '#ffffff',
                order: sqlTab.displayOrder || 0,
                active: sqlTab.isActive,
                sections: parseTabSections<AboutTabSection>(sqlTab.sections),
                navDisplayMode: sqlTab.navDisplayMode || 'panel'
              }));
              
              loadedSettings.customTabs = customTabs;
            } else {
              console.log('ℹ️ [ABOUT TABS] Henüz dinamik sekme yok');
            }
          } catch (tabsError) {
            console.error('❌ [ABOUT TABS] Sekmeler yüklenirken hata:', tabsError);
            // Hata olsa bile devam et, boş array kullan
          }

          setSettings(loadedSettings);
          if (onSettingsChange) {
            onSettingsChange({
              ...loadedSettings,
              sectionSettingsJson: serializeSectionSettingsJson(loadedSettings.sectionSettings || {}),
            });
          }
        } else {
          // Veri yoksa default ayarları kullan
          setSettings(defaultAboutSettings);
          if (onSettingsChange) {
            onSettingsChange(defaultAboutSettings);
          }
        }
      } catch (err: any) {
        console.error('Hakkımızda ayarları yüklenirken hata:', err);
        
        // 404 hatası normal (henüz kayıt yok), default ayarları kullan
        if (err.response?.status === 404 || !err.response) {
          setSettings(defaultAboutSettings);
          if (onSettingsChange) {
            onSettingsChange(defaultAboutSettings);
          }
        } else {
          console.error('Ayarlar yüklenirken bir hata oluştu:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [companyId, configId]);

  // Varsayılan ayarlara sıfırla
  const handleReset = () => {
    const newSettings = defaultAboutSettings;
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange({
        ...newSettings,
        sectionSettingsJson: serializeSectionSettingsJson(newSettings.sectionSettings || {}),
      });
    }
  };

  const updateHeroSection = (heroSection: CorporateHeroSectionSettings) => {
    setSettings((prev) => {
      const newSettings = {
        ...prev,
        heroSection,
      };
      if (onSettingsChange) {
        onSettingsChange(newSettings);
      }
      return newSettings;
    });
  };

  // Story section güncelle
  const publishSettings = (newSettings: AboutSettings) => {
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange({
        ...newSettings,
        sectionSettingsJson: serializeSectionSettingsJson(newSettings.sectionSettings || {}),
      });
    }
  };

  const updateSectionSettings = (
    sectionKey: keyof SectionSettingsPayload,
    patch: Record<string, unknown>
  ) => {
    publishSettings({
      ...settings,
      sectionSettings: {
        ...settings.sectionSettings,
        [sectionKey]: {
          ...(settings.sectionSettings?.[sectionKey] || {}),
          ...patch,
        },
      },
    });
  };

  const updateStorySection = (field: keyof typeof settings.storySection, value: any) => {
    publishSettings({
      ...settings,
      storySection: {
        ...settings.storySection,
        [field]: value
      }
    });
  };

  // Story stats güncelle
  const updateStoryStat = (statKey: keyof typeof settings.storySection.stats, value: string) => {
    publishSettings({
      ...settings,
      storySection: {
        ...settings.storySection,
        stats: {
          ...settings.storySection.stats,
          [statKey]: value
        }
      }
    });
  };

  const updateStoryStatLabel = (statKey: keyof StatsLabels, value: string) => {
    updateSectionSettings('storySection', {
      statsLabels: {
        ...(settings.sectionSettings?.storySection?.statsLabels || {}),
        [statKey]: value,
      },
    });
  };

  const uploadStoryImage = async (file: File) => {
    try {
      setStoryImageUploading(true);
      const [uploaded] = await ImageUploadService.uploadImages([file]);
      if (uploaded?.originalUrl) {
        updateSectionSettings('storySection', {
          imageEnabled: true,
          imageUrl: uploaded.originalUrl,
          imagePosition: settings.sectionSettings?.storySection?.imagePosition || 'right',
          imageShape: settings.sectionSettings?.storySection?.imageShape || 'circle',
        });
      }
    } catch (error) {
      console.error('Hikaye görseli yüklenemedi:', error);
    } finally {
      setStoryImageUploading(false);
    }
  };

  const handleValuesTemplateChange = (template: AboutValuesTemplate) => {
    const extras = settings.sectionSettings?.valuesSection || {};
    const checklist = (extras.checklist || []).map((item) => String(item || '').trim()).filter(Boolean);
    updateSectionSettings('valuesSection', {
      template,
      eyebrow: extras.eyebrow?.trim() || (template === 'quality-assurance' ? DEFAULT_VALUES_EYEBROW : extras.eyebrow),
      checklist:
        checklist.length > 0
          ? extras.checklist
          : template === 'quality-assurance'
            ? DEFAULT_VALUES_CHECKLIST
            : extras.checklist,
      headingColor:
        extras.headingColor || (template === 'text-columns' ? '#147a8c' : DEFAULT_VALUES_HEADING_COLOR),
      headingFontSize:
        typeof extras.headingFontSize === 'number' ? extras.headingFontSize : DEFAULT_VALUES_HEADING_FONT_SIZE,
      accentColor: extras.accentColor || (template === 'text-columns' ? '#64748b' : DEFAULT_VALUES_ACCENT_COLOR),
    });
  };

  const updateValuesChecklistItem = (index: number, value: string) => {
    const current = settings.sectionSettings?.valuesSection?.checklist || DEFAULT_VALUES_CHECKLIST;
    const next = [0, 1, 2].map((i) => (i === index ? value : current[i] || ''));
    updateSectionSettings('valuesSection', { checklist: next });
  };

  // Values section güncelle
  const updateValuesSection = (field: keyof typeof settings.valuesSection, value: any) => {
    publishSettings({
      ...settings,
      valuesSection: {
        ...settings.valuesSection,
        [field]: value
      }
    });
  };

  const applyValues = (values: AboutSettings['valuesSection']['values']) => {
    const nextValues = values.slice(0, 6);
    publishSettings({
      ...settings,
      valuesSection: {
        ...settings.valuesSection,
        values: nextValues,
      },
      sectionSettings: {
        ...settings.sectionSettings,
        valuesSection: {
          ...(settings.sectionSettings?.valuesSection || {}),
          items: nextValues,
        },
      },
    });
  };

  // Değer güncelle
  const updateValue = (index: number, field: keyof typeof settings.valuesSection.values[0], value: string) => {
    applyValues(
      settings.valuesSection.values.map((val, i) => (i === index ? { ...val, [field]: value } : val))
    );
  };

  const removeValue = (index: number) => {
    applyValues(settings.valuesSection.values.filter((_, i) => i !== index));
  };

  const addValue = () => {
    if (settings.valuesSection.values.length >= 6) return;
    applyValues([
      ...settings.valuesSection.values,
      {
        id: uuidv4(),
        title: 'Yeni değer',
        description: '',
        icon: 'star',
        color: '#64748b',
      },
    ]);
  };

  // Why choose section güncelle
  const updateWhyChooseSection = (field: keyof typeof settings.whyChooseSection, value: any) => {
    publishSettings({
      ...settings,
      whyChooseSection: {
        ...settings.whyChooseSection,
        [field]: value
      }
    });
  };

  const ctaSettings = resolveAboutCtaSection(settings.sectionSettings?.ctaSection);
  const updateCta = (patch: Partial<import('../utils/corporateSectionSettings').AboutCtaSectionExtras>) =>
    updateSectionSettings('ctaSection', patch as Record<string, unknown>);

  // Özellik güncelle
  const updateFeature = (index: number, field: keyof typeof settings.whyChooseSection.features[0], value: string) => {
    const newSettings = {
      ...settings,
      whyChooseSection: {
        ...settings.whyChooseSection,
        features: settings.whyChooseSection.features.map((feat, i) => 
          i === index ? { ...feat, [field]: value } : feat
        )
      }
    };
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  // Tab değişikliği
  const handleTabChange = (newValue: number) => {
    setActiveTab(newValue);
  };

  // Ana sekme sırasını kaydet (anasayfa modülleri gibi sürükle-bırak)
  const handleTabsReorder = async (reorderedItems: { id: string; name: string }[]) => {
    if (!companyId || !configId) return;

    const tabMap = new Map((settings.customTabs || []).map((tab) => [tab.id, tab]));
    const normalizedTabs = reorderedItems
      .map((item, index) => {
        const existing = tabMap.get(item.id);
        if (!existing) return null;
        return { ...existing, order: index };
      })
      .filter((tab): tab is AboutTab => tab !== null);

    const previousSettings = settings;
    const sortedPrevious = [...(settings.customTabs || [])].sort((a, b) => a.order - b.order);
    const activeTabId = activeTab > 0 ? sortedPrevious[activeTab - 1]?.id : null;
    const nextActiveIndex = activeTabId
      ? normalizedTabs.findIndex((tab) => tab.id === activeTabId) + 1
      : 0;

    const newSettings = {
      ...settings,
      customTabs: normalizedTabs,
    };

    setSettings(newSettings);
    if (activeTab > 0 && nextActiveIndex > 0) {
      setActiveTab(nextActiveIndex);
    }
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }

    try {
      setReorderingTabs(true);
      setReorderError(null);
      await Promise.all(
        normalizedTabs.map((tab) =>
          saveAboutTab({
            id: tab.id,
            configId,
            companyId,
            pageType: 'AboutTab',
            name: tab.name,
            title: tab.title,
            content: tab.content || '',
            backgroundColor: tab.backgroundColor || '#ffffff',
            displayOrder: tab.order,
            isActive: tab.active,
            sections: JSON.stringify(tab.sections || []),
            navDisplayMode: tab.navDisplayMode || 'panel',
          })
        )
      );
    } catch (error) {
      console.error('❌ [ABOUT TAB] Sıra kaydedilirken hata:', error);
      setSettings(previousSettings);
      if (onSettingsChange) {
        onSettingsChange(previousSettings);
      }
      setReorderError(t('ecommerceAboutSettings.tabs.reorderError'));
    } finally {
      setReorderingTabs(false);
    }
  };

  // Yeni sekme ekle
  const handleAddTab = async (tabName: string) => {
    if (!companyId || !configId) {
      console.error('❌ [ADD TAB] CompanyId veya ConfigId bulunamadı');
      return;
    }

    const newTabId = uuidv4();
    const newTab: AboutTab = {
      id: newTabId,
      name: tabName,
      title: tabName,
      content: '',
      backgroundColor: '#ffffff',
      order: (settings.customTabs?.length || 0) + 1,
      active: true,
      navDisplayMode: 'panel'
    };

    try {
      // ✅ SQL'e kaydet
      const saveData = {
        id: newTabId,
        configId: configId,
        companyId: companyId,
        pageType: 'AboutTab', // ✅ Hakkımızda sekmeleri için
        name: newTab.name,
        title: newTab.title,
        content: newTab.content,
        backgroundColor: newTab.backgroundColor,
        displayOrder: newTab.order,
        isActive: newTab.active,
        sections: JSON.stringify([]), // Boş sections array
        navDisplayMode: newTab.navDisplayMode
      };

      console.log('💾 [ADD TAB] SQL e yeni sekme kaydediliyor:', saveData);
      const response = await saveAboutTab(saveData);

      if (response.success) {
        console.log('✅ [ADD TAB] Sekme SQL e başarıyla kaydedildi');

        // Local state'i güncelle
        const newSettings = {
          ...settings,
          customTabs: [...(settings.customTabs || []), newTab]
        };

        setSettings(newSettings);
        
        if (onSettingsChange) {
          onSettingsChange(newSettings);
        }

        // Yeni eklenen sekmeyi aktif yap
        setActiveTab((settings.customTabs?.length || 0) + 1);
      } else {
        console.error('❌ [ADD TAB] Sekme kaydedilemedi:', response.message);
      }
    } catch (error) {
      console.error('❌ [ADD TAB] Sekme kaydetme hatası:', error);
    }
  };

  // Sekme silme başlat
  const handleDeleteTab = (tabId: string, tabName: string) => {
    setTabToDelete(tabId);
    setDeleteTabName(tabName);
    setDeleteModalOpen(true);
  };

  // Sekme silme onay
  const confirmDeleteTab = async () => {
    if (!tabToDelete || !companyId) return;

    setDeleting(true);

    try {
      // ✅ SQL'den sil
      console.log('🗑️ [DELETE TAB] SQL den sekme siliniyor:', tabToDelete, companyId);
      // 🔒 MULTI-TENANT: companyId parametresi eklendi
      const response = await deleteAboutTab(tabToDelete, companyId);

      if (response.success) {
        console.log('✅ [DELETE TAB] Sekme SQL den başarıyla silindi');

        // Local state'i güncelle
        const newSettings = {
          ...settings,
          customTabs: (settings.customTabs || []).filter(tab => tab.id !== tabToDelete)
        };

        setSettings(newSettings);
        if (onSettingsChange) {
          onSettingsChange(newSettings);
        }

        // Güvenli bir sekmeye geçiş yap
        setActiveTab(0);
        
        // Modal'ı kapat
        setDeleteModalOpen(false);
        setTabToDelete(null);
        setDeleteTabName('');
      } else {
        console.error('❌ [DELETE TAB] Sekme silinemedi:', response.message);
      }
      
    } catch (error) {
      console.error('❌ [DELETE TAB] Sekme silme hatası:', error);
    } finally {
      setDeleting(false);
    }
  };

  // Sekme silme iptal
  const cancelDeleteTab = () => {
    setDeleteModalOpen(false);
    setTabToDelete(null);
    setDeleteTabName('');
  };

  // Sekme güncelle (otomatik SQL'e kaydet)
  const updateTab = async (tabId: string, field: keyof AboutTab, value: any) => {
    if (!companyId || !configId) {
      console.error('❌ [UPDATE TAB] CompanyId veya ConfigId bulunamadı');
      return;
    }

    // Local state'i güncelle
    const newSettings = {
      ...settings,
      customTabs: (settings.customTabs || []).map(tab => 
        tab.id === tabId ? { ...tab, [field]: value } : tab
      )
    };

    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }

    // ✅ SQL'e otomatik kaydet
    try {
      const updatedTab = newSettings.customTabs?.find(tab => tab.id === tabId);
      if (!updatedTab) return;

      const saveData = {
        id: updatedTab.id,
        configId: configId,
        companyId: companyId,
        pageType: 'AboutTab', // ✅ Hakkımızda sekmeleri için
        name: updatedTab.name,
        title: updatedTab.title,
        content: updatedTab.content,
        backgroundColor: updatedTab.backgroundColor,
        displayOrder: updatedTab.order,
        isActive: updatedTab.active,
        sections: JSON.stringify(updatedTab.sections || []),
        navDisplayMode: updatedTab.navDisplayMode || 'panel'
      };

      console.log('💾 [UPDATE TAB] SQL e sekme güncelleniyor:', tabId);
      const response = await saveAboutTab(saveData);

      if (response.success) {
        console.log('✅ [UPDATE TAB] Sekme SQL e başarıyla güncellendi');
      } else {
        console.error('❌ [UPDATE TAB] Sekme güncellenemedi:', response.message);
      }
    } catch (error) {
      console.error('❌ [UPDATE TAB] Sekme güncelleme hatası:', error);
    }
  };

  // Sekme aktif/pasif
  const toggleTabActive = (tabId: string) => {
    updateTab(tabId, 'active', !(settings.customTabs?.find(tab => tab.id === tabId)?.active || false));
  };

  // Yeni bölüm ekle (otomatik SQL'e kaydet)
  const addNewSection = async (tabId: string) => {
    if (!companyId || !configId) return;

    const newSection: AboutTabSection = {
      id: uuidv4(),
      title: '',
      content: '',
      image: '',
      imagePosition: 'left',
      imageSize: 'equal',
      imageAnimation: 'zoom',
    };

    const newSettings = {
      ...settings,
      customTabs: (settings.customTabs || []).map(tab => 
        tab.id === tabId 
          ? { ...tab, sections: [...(tab.sections || []), newSection] }
          : tab
      )
    };

    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }

    // ✅ SQL'e otomatik kaydet
    try {
      const updatedTab = newSettings.customTabs?.find(tab => tab.id === tabId);
      if (!updatedTab) return;

      const saveData = {
        id: updatedTab.id,
        configId: configId,
        companyId: companyId,
        pageType: 'AboutTab', // ✅ Hakkımızda sekmeleri için
        name: updatedTab.name,
        title: updatedTab.title,
        content: updatedTab.content,
        backgroundColor: updatedTab.backgroundColor,
        displayOrder: updatedTab.order,
        isActive: updatedTab.active,
        sections: JSON.stringify(updatedTab.sections || []),
        navDisplayMode: updatedTab.navDisplayMode || 'panel'
      };

      await saveAboutTab(saveData);
      console.log('✅ [ADD SECTION] Yeni bölüm SQL e kaydedildi');
    } catch (error) {
      console.error('❌ [ADD SECTION] Bölüm ekleme hatası:', error);
    }
  };

  // Bölüm sil (otomatik SQL'e kaydet)
  const deleteSection = async (tabId: string, sectionId: string) => {
    if (!companyId || !configId) return;

    const newSettings = {
      ...settings,
      customTabs: (settings.customTabs || []).map(tab => 
        tab.id === tabId 
          ? { ...tab, sections: (tab.sections || []).filter(section => section.id !== sectionId) }
          : tab
      )
    };

    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }

    // ✅ SQL'e otomatik kaydet
    try {
      const updatedTab = newSettings.customTabs?.find(tab => tab.id === tabId);
      if (!updatedTab) return;

      const saveData = {
        id: updatedTab.id,
        configId: configId,
        companyId: companyId,
        pageType: 'AboutTab', // ✅ Hakkımızda sekmeleri için
        name: updatedTab.name,
        title: updatedTab.title,
        content: updatedTab.content,
        backgroundColor: updatedTab.backgroundColor,
        displayOrder: updatedTab.order,
        isActive: updatedTab.active,
        sections: JSON.stringify(updatedTab.sections || []),
        navDisplayMode: updatedTab.navDisplayMode || 'panel'
      };

      await saveAboutTab(saveData);
      console.log('✅ [DELETE SECTION] Bölüm silindi ve SQL e kaydedildi');
    } catch (error) {
      console.error('❌ [DELETE SECTION] Bölüm silme hatası:', error);
    }
  };

  // Bölüm güncelle (otomatik SQL'e kaydet)
  const updateSection = async (tabId: string, sectionId: string, field: keyof AboutTabSection, value: string) => {
    if (!companyId || !configId) return;

    const newSettings = {
      ...settings,
      customTabs: (settings.customTabs || []).map(tab => 
        tab.id === tabId 
          ? { 
              ...tab, 
              sections: (tab.sections || []).map(section => 
                section.id === sectionId 
                  ? { ...section, [field]: value }
                  : section
              )
            }
          : tab
      )
    };

    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }

    // ✅ SQL'e otomatik kaydet
    try {
      const updatedTab = newSettings.customTabs?.find(tab => tab.id === tabId);
      if (!updatedTab) return;

      const saveData = {
        id: updatedTab.id,
        configId: configId,
        companyId: companyId,
        pageType: 'AboutTab',
        name: updatedTab.name,
        title: updatedTab.title,
        content: updatedTab.content,
        backgroundColor: updatedTab.backgroundColor,
        displayOrder: updatedTab.order,
        isActive: updatedTab.active,
        sections: JSON.stringify(updatedTab.sections || []),
        navDisplayMode: updatedTab.navDisplayMode || 'panel'
      };

      await saveAboutTab(saveData);
      console.log('✅ [UPDATE SECTION] Bölüm güncellendi ve SQL e kaydedildi');
    } catch (error) {
      console.error('❌ [UPDATE SECTION] Bölüm güncelleme hatası:', error);
    }
  };

  const reorderSections = async (tabId: string, nextSections: AboutTabSection[]) => {
    if (!companyId || !configId) return;

    const newSettings = {
      ...settings,
      customTabs: (settings.customTabs || []).map(tab =>
        tab.id === tabId
          ? { ...tab, sections: nextSections }
          : tab
      )
    };

    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }

    try {
      const updatedTab = newSettings.customTabs?.find(tab => tab.id === tabId);
      if (!updatedTab) return;

      await saveAboutTab({
        id: updatedTab.id,
        configId: configId,
        companyId: companyId,
        pageType: 'AboutTab',
        name: updatedTab.name,
        title: updatedTab.title,
        content: updatedTab.content,
        backgroundColor: updatedTab.backgroundColor,
        displayOrder: updatedTab.order,
        isActive: updatedTab.active,
        sections: JSON.stringify(updatedTab.sections || []),
        navDisplayMode: updatedTab.navDisplayMode || 'panel'
      });
    } catch (error) {
      console.error('❌ [REORDER SECTIONS] Bölüm sırası kaydedilemedi:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // ⚠️ configId kontrolü - eğer yoksa uyarı göster
  if (!configId) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            ⚠️ {t('ecommerceAboutSettings.config.warningTitle')}
          </Typography>
          <Typography variant="body2">
            {t('ecommerceAboutSettings.config.warningDescription')}
          </Typography>
        </Alert>
      </Paper>
    );
  }

  const customTabs = (settings.customTabs || []).sort((a, b) => a.order - b.order);

  return (
    <Paper elevation={3} sx={{ position: 'relative', zIndex: 1 }}>
      {/* Tab Header */}
      <Box sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          pt: 1,
          px: 1,
          backgroundColor: theme.palette.grey[200],
      }}>
          <SortableSubTabs
            generalLabel={t('ecommerceAboutSettings.tabs.aboutSettings')}
            tabs={customTabs.map((tab) => ({ id: tab.id, name: tab.name }))}
            activeIndex={activeTab}
            reordering={reorderingTabs}
            dragHandleTitle={t('ecommerceAboutSettings.tabs.dragToReorder')}
            ariaLabel={t('ecommerceAboutSettings.tabs.ariaLabel')}
            onSelectTab={handleTabChange}
            onReorder={handleTabsReorder}
          />
          {reorderingTabs && (
            <CircularProgress size={18} sx={{ ml: 1, flexShrink: 0 }} />
          )}
          <Tooltip title={t('ecommerceAboutSettings.tabs.addNewTab')}>
              <IconButton
                size="small"
                sx={{ ml: 1, flexShrink: 0 }}
                onClick={() => setIsModalOpen(true)}
                disabled={reorderingTabs}
              >
                  <AddIcon />
              </IconButton>
          </Tooltip>
      </Box>

      {reorderError && (
        <Alert severity="error" sx={{ m: 2 }} onClose={() => setReorderError(null)}>
          {reorderError}
        </Alert>
      )}

      {/* Ana Hakkımızda Ayarları Tab */}
      <TabPanel value={activeTab} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" fontWeight="bold">
            <InfoIcon sx={{ mr: 1.5, color: 'primary.main' }} />
            {t('ecommerceAboutSettings.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={t('ecommerceAboutSettings.actions.resetToDefaultSettings')}>
              <IconButton onClick={handleReset} color="secondary">
                <RestartAltIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

      {/* Hero Section */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceAboutSettings.sections.mainHeader')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CorporateHeroSettingsPanel
            value={settings.heroSection}
            onChange={updateHeroSection}
            layoutMode="color-wash"
            companyId={companyId}
            configId={configId}
            defaultHeight={500}
            sectionOptions={buildHeroSectionOptions(
              [
                {
                  label: settings.storySection?.title || t('ecommerceAboutSettings.sections.ourStory'),
                  value: 'about-story',
                },
                {
                  label: settings.valuesSection?.title || t('ecommerceAboutSettings.sections.ourValues'),
                  value: 'about-values',
                },
                {
                  label: settings.whyChooseSection?.title || t('ecommerceAboutSettings.sections.whyChooseUs'),
                  value: 'about-why-choose',
                },
                {
                  label: resolveAboutCtaSection(settings.sectionSettings?.ctaSection).title
                    || t('ecommerceAboutSettings.sections.cta'),
                  value: 'about-cta',
                },
                { label: 'SSS', value: 'about_faq' },
              ],
              settings.customTabs
            )}
          />
        </AccordionDetails>
      </Accordion>

      {/* Story Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceAboutSettings.sections.ourStory')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SectionVisibilityToggle
                enabled={settings.sectionSettings?.storySection?.enabled !== false}
                onChange={(enabled) => updateSectionSettings('storySection', { enabled })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.fields.title')}
                value={settings.storySection.title}
                onChange={(e) => updateStorySection('title', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('ecommerceAboutSettings.fields.description')}
                value={settings.storySection.description}
                onChange={(e) => updateStorySection('description', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.fields.backgroundColor')}
                value={settings.storySection.backgroundColor}
                onChange={(e) => updateStorySection('backgroundColor', e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        component="input"
                        type="color"
                        value={settings.storySection.backgroundColor}
                        onChange={(e) => updateStorySection('backgroundColor', e.target.value)}
                        sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.sectionSettings?.storySection?.imageEnabled === true}
                    onChange={(e) =>
                      updateSectionSettings('storySection', {
                        imageEnabled: e.target.checked,
                        imagePosition: settings.sectionSettings?.storySection?.imagePosition || 'right',
                        imageShape: settings.sectionSettings?.storySection?.imageShape || 'circle',
                      })
                    }
                  />
                }
                label="Hikaye metninin yanında görsel göster"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5 }}>
                Görsel, hikaye başlığı ve açıklamasının sağına veya soluna yerleşir.
              </Typography>
            </Grid>

            {settings.sectionSettings?.storySection?.imageEnabled === true && (
              <>
                <Grid item xs={12} md={5}>
                  <Box
                    sx={{
                      width: 180,
                      height: settings.sectionSettings?.storySection?.imageShape === 'rounded' ? 220 : 180,
                      mx: 'auto',
                      borderRadius: settings.sectionSettings?.storySection?.imageShape === 'rounded' ? '28px' : '50%',
                      overflow: 'hidden',
                      bgcolor: '#f1f5f9',
                      boxShadow: '0 16px 40px rgba(15, 23, 42, 0.16)',
                      border: '8px solid #fff',
                      outline: '1px solid #e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {settings.sectionSettings?.storySection?.imageUrl ? (
                      <Box
                        component="img"
                        src={settings.sectionSettings.storySection.imageUrl}
                        alt="Hikaye görseli"
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <ImageIcon sx={{ color: 'text.disabled', fontSize: 40 }} />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={7}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={storyImageUploading ? <CircularProgress size={16} /> : <CloudUpload />}
                      disabled={storyImageUploading}
                    >
                      {storyImageUploading ? 'Yükleniyor' : 'Görsel yükle'}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) void uploadStoryImage(file);
                          e.target.value = '';
                        }}
                      />
                    </Button>
                    {settings.sectionSettings?.storySection?.imageUrl && (
                      <Button
                        color="inherit"
                        startIcon={<DeleteIcon />}
                        onClick={() => updateSectionSettings('storySection', { imageUrl: '' })}
                      >
                        Kaldır
                      </Button>
                    )}
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    label="Görsel adresi"
                    value={settings.sectionSettings?.storySection?.imageUrl || ''}
                    onChange={(e) => updateSectionSettings('storySection', { imageUrl: e.target.value })}
                    placeholder="https://..."
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Konum
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={settings.sectionSettings?.storySection?.imagePosition || 'right'}
                    onChange={(_e, value) => {
                      if (value) updateSectionSettings('storySection', { imagePosition: value });
                    }}
                    sx={{ mb: 2 }}
                  >
                    <ToggleButton value="left" sx={{ textTransform: 'none', px: 2 }}>
                      Solda
                    </ToggleButton>
                    <ToggleButton value="right" sx={{ textTransform: 'none', px: 2 }}>
                      Sağda
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Çerçeve
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={settings.sectionSettings?.storySection?.imageShape || 'circle'}
                    onChange={(_e, value) => {
                      if (value) updateSectionSettings('storySection', { imageShape: value });
                    }}
                  >
                    <ToggleButton value="circle" sx={{ textTransform: 'none', px: 2 }}>
                      Yuvarlak
                    </ToggleButton>
                    <ToggleButton value="rounded" sx={{ textTransform: 'none', px: 2 }}>
                      Yumuşak köşe
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              </>
            )}
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <SectionVisibilityToggle
                label="İstatistik Paneli Görünürlüğü"
                enabled={settings.sectionSettings?.storySection?.statsEnabled !== false}
                onChange={(statsEnabled) => updateSectionSettings('storySection', { statsEnabled })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ColorPalettePicker
                label="İstatistik Paneli Arka Plan Rengi"
                value={
                  settings.sectionSettings?.storySection?.statsBackgroundColor ||
                  DEFAULT_STATS_BACKGROUND_COLOR
                }
                onChange={(statsBackgroundColor) =>
                  updateSectionSettings('storySection', {
                    statsEnabled: statsBackgroundColor ? true : false,
                    statsBackgroundColor,
                  })
                }
                allowClear
                clearLabel="Paneli Kaldır"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ColorPalettePicker
                label="İstatistik Paneli Yazı Rengi"
                value={
                  settings.sectionSettings?.storySection?.statsTextColor ||
                  DEFAULT_STATS_TEXT_COLOR
                }
                onChange={(statsTextColor) =>
                  updateSectionSettings('storySection', { statsTextColor })
                }
                presets={TEXT_COLOR_PRESETS}
                customLabel="Yazı rengi"
                helperText="Açık arka plan için koyu, koyu arka plan için açık renk seçin"
                fallbackSolidColor={DEFAULT_STATS_TEXT_COLOR}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.sectionSettings?.storySection?.statsCellBorderEnabled === true}
                    onChange={(e) =>
                      updateSectionSettings('storySection', {
                        statsCellBorderEnabled: e.target.checked,
                      })
                    }
                    size="small"
                  />
                }
                label="Veri kutucuklarında kenarlık göster"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5 }}>
                Kapalıyken kutucuklar yalnızca gölge ile ayrılır.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t('ecommerceAboutSettings.sections.statistics')}
              </Typography>
            </Grid>
            
            {([
              { key: 'customers' as const, valueKey: 'customers' as const },
              { key: 'products' as const, valueKey: 'products' as const },
              { key: 'support' as const, valueKey: 'support' as const },
              { key: 'satisfaction' as const, valueKey: 'satisfaction' as const },
            ]).map((item) => (
              <Grid item xs={12} md={3} key={item.key}>
                <TextField
                  fullWidth
                  label="Başlık"
                  value={
                    settings.sectionSettings?.storySection?.statsLabels?.[item.key] ??
                    DEFAULT_STATS_LABELS[item.key]
                  }
                  onChange={(e) => updateStoryStatLabel(item.key, e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ mb: 1 }}
                />
                <TextField
                  fullWidth
                  label="Değer"
                  value={settings.storySection.stats[item.valueKey]}
                  onChange={(e) => updateStoryStat(item.valueKey, e.target.value)}
                  variant="outlined"
                />
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Values Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <StarIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceAboutSettings.sections.ourValues')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SectionVisibilityToggle
                enabled={settings.sectionSettings?.valuesSection?.enabled !== false}
                onChange={(enabled) => updateSectionSettings('valuesSection', { enabled })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Bölüm şablonu
              </Typography>
              <AboutValuesTemplatePicker
                value={normalizeAboutValuesTemplate(settings.sectionSettings?.valuesSection?.template)}
                onChange={handleValuesTemplateChange}
              />
            </Grid>
            {normalizeAboutValuesTemplate(settings.sectionSettings?.valuesSection?.template) === 'text-columns' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Önizleme
                  </Typography>
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3, bgcolor: settings.valuesSection.backgroundColor || '#f4f7fb' }}>
                    <Typography
                      align="center"
                      sx={{
                        fontWeight: 800,
                        color: settings.sectionSettings?.valuesSection?.headingColor || '#147a8c',
                        fontSize: Math.min(settings.sectionSettings?.valuesSection?.headingFontSize || DEFAULT_VALUES_HEADING_FONT_SIZE, 40),
                        lineHeight: 1.15,
                      }}
                    >
                      {settings.valuesSection.title || 'Hakkımızda'}
                    </Typography>
                    <Typography align="center" sx={{ mt: 1, color: settings.sectionSettings?.valuesSection?.accentColor || '#64748b' }}>
                      {settings.sectionSettings?.valuesSection?.eyebrow || 'Alt başlık'}
                    </Typography>
                    <Typography align="center" variant="body2" color="text.secondary" sx={{ mt: 2, maxWidth: 520, mx: 'auto' }}>
                      {settings.valuesSection.description}
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: previewValueColumns(settings.valuesSection.values.filter((item) => item.title?.trim()).length),
                        },
                        mt: 3,
                      }}
                    >
                      {settings.valuesSection.values.filter((item) => item.title?.trim()).map((item, index, visible) => (
                        <Box
                          key={item.id || index}
                          sx={{
                            px: 2,
                            py: 1.5,
                            textAlign: 'center',
                            borderLeft: previewValueBorderLeft(visible.length, index),
                            borderTop: previewValueBorderTop(visible.length, index),
                            gridColumn: previewValueSpan(visible.length, index),
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight={800}>
                            {item.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Alt başlık"
                    value={settings.sectionSettings?.valuesSection?.eyebrow || ''}
                    onChange={(e) => updateSectionSettings('valuesSection', { eyebrow: e.target.value })}
                    variant="outlined"
                    helperText="Başlığın hemen altındaki satır. Örn. Kim Olduğumuzu Keşfedin"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Başlık boyutu (px)"
                    value={settings.sectionSettings?.valuesSection?.headingFontSize || DEFAULT_VALUES_HEADING_FONT_SIZE}
                    onChange={(e) => updateSectionSettings('valuesSection', { headingFontSize: Number(e.target.value) || DEFAULT_VALUES_HEADING_FONT_SIZE })}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <ColorPalettePicker
                    label="Başlık rengi"
                    value={settings.sectionSettings?.valuesSection?.headingColor || '#147a8c'}
                    onChange={(headingColor) => updateSectionSettings('valuesSection', { headingColor })}
                    presets={[
                      { label: 'Turkuaz', value: '#147a8c' },
                      { label: 'Orman', value: DEFAULT_VALUES_HEADING_COLOR },
                      { label: 'Lacivert', value: '#0f172a' },
                    ]}
                    customLabel="Başlık rengi"
                    fallbackSolidColor="#147a8c"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <ColorPalettePicker
                    label="Alt başlık rengi"
                    value={settings.sectionSettings?.valuesSection?.accentColor || '#64748b'}
                    onChange={(accentColor) => updateSectionSettings('valuesSection', { accentColor })}
                    presets={[
                      { label: 'Gri', value: '#64748b' },
                      { label: 'Koyu gri', value: '#475569' },
                      { label: 'Turkuaz', value: '#147a8c' },
                    ]}
                    customLabel="Alt başlık rengi"
                    fallbackSolidColor="#64748b"
                  />
                </Grid>
              </>
            )}
            {normalizeAboutValuesTemplate(settings.sectionSettings?.valuesSection?.template) === 'quality-assurance' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Üst etiket"
                    value={settings.sectionSettings?.valuesSection?.eyebrow ?? DEFAULT_VALUES_EYEBROW}
                    onChange={(e) => updateSectionSettings('valuesSection', { eyebrow: e.target.value })}
                    variant="outlined"
                    helperText="Örn. KALİTE GÜVENCESİ"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <ColorPalettePicker
                    label="Başlık rengi"
                    value={settings.sectionSettings?.valuesSection?.headingColor || DEFAULT_VALUES_HEADING_COLOR}
                    onChange={(headingColor) => updateSectionSettings('valuesSection', { headingColor })}
                    presets={[
                      { label: 'Orman', value: DEFAULT_VALUES_HEADING_COLOR },
                      { label: 'Koyu', value: '#0f172a' },
                      { label: 'Antrasit', value: '#1e293b' },
                    ]}
                    customLabel="Başlık rengi"
                    helperText="Soldaki büyük başlık ve kart başlıkları"
                    fallbackSolidColor={DEFAULT_VALUES_HEADING_COLOR}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <ColorPalettePicker
                    label="Etiket rengi"
                    value={settings.sectionSettings?.valuesSection?.accentColor || DEFAULT_VALUES_ACCENT_COLOR}
                    onChange={(accentColor) => updateSectionSettings('valuesSection', { accentColor })}
                    presets={[
                      { label: 'Altın', value: DEFAULT_VALUES_ACCENT_COLOR },
                      { label: 'Bronz', value: '#9a7348' },
                      { label: 'Yeşil', value: '#2d6a4f' },
                    ]}
                    customLabel="Etiket rengi"
                    helperText="Üst etiket (KALİTE GÜVENCESİ)"
                    fallbackSolidColor={DEFAULT_VALUES_ACCENT_COLOR}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Madde listesi
                  </Typography>
                </Grid>
                {[0, 1, 2].map((index) => (
                  <Grid item xs={12} key={`values-check-${index}`}>
                    <TextField
                      fullWidth
                      label={`Madde ${index + 1}`}
                      value={(settings.sectionSettings?.valuesSection?.checklist || DEFAULT_VALUES_CHECKLIST)[index] || ''}
                      onChange={(e) => updateValuesChecklistItem(index, e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                ))}
              </>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.fields.title')}
                value={settings.valuesSection.title}
                onChange={(e) => updateValuesSection('title', e.target.value)}
                variant="outlined"
                helperText={
                  normalizeAboutValuesTemplate(settings.sectionSettings?.valuesSection?.template) === 'quality-assurance'
                    ? 'Kalite Güvencesi şablonunda bu metin soldaki büyük başlık olarak görünür.'
                    : normalizeAboutValuesTemplate(settings.sectionSettings?.valuesSection?.template) === 'text-columns'
                      ? 'Metin Sütunları şablonunda sayfanın ortasındaki büyük başlık.'
                      : undefined
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label={t('ecommerceAboutSettings.fields.description')}
                value={settings.valuesSection.description}
                onChange={(e) => updateValuesSection('description', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.fields.backgroundColor')}
                value={settings.valuesSection.backgroundColor}
                onChange={(e) => updateValuesSection('backgroundColor', e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        component="input"
                        type="color"
                        value={settings.valuesSection.backgroundColor}
                        onChange={(e) => updateValuesSection('backgroundColor', e.target.value)}
                        sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t('ecommerceAboutSettings.sections.values')}
              </Typography>
            </Grid>
            
            {settings.valuesSection.values.map((value, index) => (
              <Grid item xs={12} key={value.id}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label="Değeri kaldır"
                      onClick={() => removeValue(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label={t('ecommerceAboutSettings.fields.title')}
                        value={value.title}
                        onChange={(e) => updateValue(index, 'title', e.target.value)}
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                    {normalizeAboutValuesTemplate(settings.sectionSettings?.valuesSection?.template) !== 'text-columns' && (
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>İkon</InputLabel>
                        <Select
                          label="İkon"
                          value={value.icon || 'star'}
                          onChange={(e) => updateValue(index, 'icon', String(e.target.value))}
                        >
                          {ABOUT_VALUE_ICONS.map((icon) => (
                            <MenuItem key={icon.value} value={icon.value}>
                              {icon.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    )}
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label={t('ecommerceAboutSettings.fields.description')}
                        value={value.description}
                        onChange={(e) => updateValue(index, 'description', e.target.value)}
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label={t('ecommerceAboutSettings.fields.color')}
                        value={value.color}
                        onChange={(e) => updateValue(index, 'color', e.target.value)}
                        variant="outlined"
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Box
                                component="input"
                                type="color"
                                value={value.color}
                                onChange={(e) => updateValue(index, 'color', e.target.value)}
                                sx={{ border: 'none', width: '20px', height: '20px', p: 0, background: 'none', cursor: 'pointer' }}
                              />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            ))}
            {settings.valuesSection.values.length < 6 && (
              <Grid item xs={12}>
                <Button startIcon={<AddIcon />} onClick={addValue}>
                  Değer ekle
                </Button>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Why Choose Section */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceAboutSettings.sections.whyChooseUs')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SectionVisibilityToggle
                enabled={settings.sectionSettings?.whyChooseSection?.enabled !== false}
                onChange={(enabled) => updateSectionSettings('whyChooseSection', { enabled })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.fields.title')}
                value={settings.whyChooseSection.title}
                onChange={(e) => updateWhyChooseSection('title', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label={t('ecommerceAboutSettings.fields.description')}
                value={settings.whyChooseSection.description}
                onChange={(e) => updateWhyChooseSection('description', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.fields.backgroundColor')}
                value={settings.whyChooseSection.backgroundColor}
                onChange={(e) => updateWhyChooseSection('backgroundColor', e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        component="input"
                        type="color"
                        value={settings.whyChooseSection.backgroundColor}
                        onChange={(e) => updateWhyChooseSection('backgroundColor', e.target.value)}
                        sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t('ecommerceAboutSettings.sections.features')}
              </Typography>
            </Grid>
            
            {settings.whyChooseSection.features.map((feature, index) => (
              <Grid item xs={12} md={6} key={feature.id}>
                <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t('ecommerceAboutSettings.fields.title')}
                        value={feature.title}
                        onChange={(e) => updateFeature(index, 'title', e.target.value)}
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label={t('ecommerceAboutSettings.fields.description')}
                        value={feature.description}
                        onChange={(e) => updateFeature(index, 'description', e.target.value)}
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <CampaignIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('ecommerceAboutSettings.sections.cta')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CorporateCtaSettingsPanel
            value={ctaSettings}
            onChange={updateCta}
          />
        </AccordionDetails>
      </Accordion>
      </TabPanel>

      {/* Özel Sekme Tab'ları */}
      {customTabs.map((tab, index) => (
        <TabPanel key={tab.id} value={activeTab} index={index + 1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              <TabIcon sx={{ mr: 1.5, color: 'primary.main' }} />
              {t('ecommerceAboutSettings.customTabs.tabSettingsTitle', { tabName: tab.name })}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={t('ecommerceAboutSettings.customTabs.deleteThisTab')}>
                <IconButton 
                  onClick={() => handleDeleteTab(tab.id, tab.name)} 
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.customTabs.tabNameLabel')}
                value={tab.name}
                onChange={(e) => updateTab(tab.id, 'name', e.target.value)}
                variant="outlined"
                placeholder="Vizyonumuz"
                helperText={t('ecommerceAboutSettings.customTabs.tabNameHelper')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.customTabs.pageTitle')}
                value={tab.title}
                onChange={(e) => updateTab(tab.id, 'title', e.target.value)}
                variant="outlined"
                placeholder="Vizyonumuz ve Hedeflerimiz"
                helperText={t('ecommerceAboutSettings.customTabs.pageTitleHelper')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('ecommerceAboutSettings.fields.backgroundColor')}
                value={tab.backgroundColor}
                onChange={(e) => updateTab(tab.id, 'backgroundColor', e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        component="input"
                        type="color"
                        value={tab.backgroundColor}
                        onChange={(e) => updateTab(tab.id, 'backgroundColor', e.target.value)}
                        sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2">{t('ecommerceAboutSettings.customTabs.status')}</Typography>
                <Button
                  variant={tab.active ? "contained" : "outlined"}
                  color={tab.active ? "success" : "warning"}
                  onClick={() => toggleTabActive(tab.id)}
                  startIcon={tab.active ? <VisibilityIcon /> : <VisibilityOffIcon />}
                >
                  {tab.active ? t('ecommerceAboutSettings.customTabs.active') : t('ecommerceAboutSettings.customTabs.hidden')}
                </Button>
              </Box>
            </Grid>
            {/* Ana İçerik - HTML Editörü */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                {t('ecommerceAboutSettings.customTabs.mainTabContent')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                {t('ecommerceAboutSettings.customTabs.mainTabContentHelper')}
              </Typography>
              <HTMLEditor
                value={tab.content || ''}
                onChange={(value) => updateTab(tab.id, 'content', value)}
                placeholder={t('ecommerceAboutSettings.customTabs.mainTabContentPlaceholder')}
                companyId={companyId}
              />
            </Grid>

            {/* Dinamik Bölümler */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <SortableCustomTabSections
                sections={tab.sections || []}
                onAdd={() => addNewSection(tab.id)}
                onDelete={(sectionId) => deleteSection(tab.id, sectionId)}
                onUpdate={(sectionId, field, value) => updateSection(tab.id, sectionId, field, value)}
                onReorder={(nextSections) => reorderSections(tab.id, nextSections)}
                titlePlaceholder={t('ecommerceAboutSettings.customTabs.sectionTitlePlaceholder')}
                contentPlaceholder={t('ecommerceAboutSettings.customTabs.sectionContentPlaceholder')}
              />
            </Grid>
          </Grid>

          {/* Durum Göstergesi */}
          <Box sx={{ mt: 3, p: 2, backgroundColor: tab.active ? '#e8f5e8' : '#fff3cd', borderRadius: 1 }}>
            <Typography variant="body2" color={tab.active ? 'success.main' : 'warning.main'}>
              {tab.active
                ? t('ecommerceAboutSettings.customTabs.activeInfo')
                : t('ecommerceAboutSettings.customTabs.hiddenInfo')}
            </Typography>
          </Box>
        </TabPanel>
      ))}

      {/* Yeni Sekme Ekleme Modal'ı */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('ecommerceAboutSettings.modals.addNewTabTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('ecommerceAboutSettings.modals.addNewTabDescription')}
          </DialogContentText>
          <NewTabInput 
            onAddTab={(tabName) => {
              handleAddTab(tabName);
              setIsModalOpen(false);
            }}
            onCancel={() => setIsModalOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Sekme Silme Onay Modal'ı */}
      <Dialog
        open={deleteModalOpen}
        onClose={!deleting ? cancelDeleteTab : undefined}
        aria-labelledby="delete-tab-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="delete-tab-dialog-title">
          {t('ecommerceAboutSettings.modals.deleteTabTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('ecommerceAboutSettings.modals.deleteTabConfirm', { tabName: deleteTabName })}
          </DialogContentText>
          <DialogContentText sx={{ mt: 1, color: 'warning.main', fontWeight: 'medium' }}>
            {t('ecommerceAboutSettings.modals.deleteTabWarning')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={cancelDeleteTab} 
            disabled={deleting}
            color="primary"
          >
            {t('ecommerceAboutSettings.actions.cancel')}
          </Button>
          <Button 
            onClick={confirmDeleteTab} 
            disabled={deleting}
            color="error"
            variant="contained"
            autoFocus
          >
            {deleting ? t('ecommerceAboutSettings.actions.deleting') : t('ecommerceAboutSettings.actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AboutSettingsComponent;
